import { CommonEnums } from 'dashboard-services/api/common';
import { DataPrepReadableConsts } from 'dashboard-services/api/dataPrep';
import { DataStorageEnums, DataStorageReadableConsts } from 'dashboard-services/api/dataStorage';
import { DataTransformEnums } from 'dashboard-services/api/dataTransform';
import { FormulaEnums } from 'dashboard-services/api/formula';
import { InsightEnums } from 'dashboard-services/api/insight';
import { CommonService, ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';

import properties from 'resources/constants/properties.json';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc)

export default (() => {
  const SQL_EDITOR_STATUSES = {
    ERROR: 'error',
    IDLE: 'idle',
    RUNNING: 'running',
    STOPPED: 'stopped',
    SUCCESS: 'success'
  }

  const BASIC_CHART_TYPES = {
    COMPOSED: {
      label: "Advanced Chart",
      value: "COMPOSED"
    },
    SEASONALITY: {
      label: "Seasonality Chart",
      value: "SEASONALITY"
    },
    QUICK: {
      label: "Quick Chart",
      value: "QUICK"
    },
    FORWARD_CURVE: {
      label: "Forward Curve Chart",
      value: "FORWARD_CURVE"
    }
  }

  const CHART_SOURCES = {
    DATA_PREP: {
      label: "Data prep",
      value: "DATA_PREP"
    },
    FILE: {
      label: "File",
      value: "FILE"
    },
    SQL_CREATOR: {
      label: "SQL Creator",
      value: "SQL_CREATOR"
    },
    SOURCE_TILE: {
      label: "Source tile",
      value: "SOURCE_TILE"
    },
    CURVE_CREATOR: {
      label: "Curve creator",
      value: "CURVE_CREATOR"
    }
  }

  const CHART_TYPES = {
    AREA: {
      label: "Area",
      value: "area"
    },
    BAR: {
      label: "Bar",
      value: "bars"
    },
    LINE: {
      label: "Line",
      value: "line"
    },
    SCATTER: {
      label: "Scatter",
      value: "scatter"
    }
  }

  const BASIC_INPUT_PARAM_TYPES = {
    FILE: {
      label: "File based",
      value: "FILE"
    },
    TS: {
      label: "Timeseries based",
      value: "TS"
    },
    CURVE: {
      label: "Curves based",
      value: "CURVE"
    },
    SQL: {
      label: "SQL Creator",
      value: "SQL"
    }
  }

  const BASIC_SEARCH_TYPES = {
    TS: {
      label: "Timeseries based",
      value: "TS"
    },
    CURVE: {
      label: "Curves based",
      value: "CURVE"
    }
  }

  const APPLY_TO = {
    CURRENT: {
      label: "Current year",
      value: "CURRENT"
    },
    CURRENT_CONTRACT_YEAR: {
      label: "Current year (contract)",
      value: "CURRENT_CONTRACT_YEAR"
    },
    FORECAST: {
      label: "Forecast",
      value: "FORECAST"
    },
    PREVIOUS: {
      label: "Previous years",
      value: "PREVIOUS"
    }
  }

  const getSeasonalityYearOptions = () => {
    const currentYear = dayjs().year()
    return [APPLY_TO.CURRENT].concat(Array.from({ length: 20 }, (_, index) => {
      const year = currentYear - index
      return {
        label: year,
        value: year
      }
    }))
  }

  const getSeasonalityYearValue = value => value === APPLY_TO.CURRENT.value ? String(dayjs().year()) : String(value)
  
  const SEASONALITY_TYPE = {
    CALENDAR: {
      value: "CALENDAR",
      label: "Calendar"
    },
    EXPIRY: {
      value: "EXPIRY",
      label: "Expire date"
    }
  }

  const SEASONALITY_PICKERS_DATA_LABEL_TYPES = {
    DEFAULT: {
      value: "DEFAULT",
      label: "Default"
    },
    KEY: {
      value: "KEY",
      label: "Key"
    },
    KEY_COMBINATION: {
      value: "KEY_COMBINATION",
      label: "Key combination"
    },
    CONTRACT_RESOLVER: {
      value: "CONTRACT_RESOLVER",
      label: "Contract resolver"
    }
  }

  const DATA_POINT_LABEL_VALUE_TYPES = {
    NORMALIZED: "NORMALIZED",
    RAW: "RAW"
  }

  const SEASONALITY_PICKERS_CONTRACT_RESOLVER_TYPES = {
    ROOT: {
      value: "ROOT",
      label: "Root"
    },
    CONTRACT_TYPE: {
      value: "CONTRACT_TYPE",
      label: "Contract type"
    },
    DELIVERY: {
      value: "DELIVERY",
      label: "Delivery"
    },
    DATE: {
      value: "DATE",
      label: "Date"
    }
  }

  const CONTRACT_RESOLVER_CONFIG_FIELD_NAMES = {
    SEASONALITY_PICKERS: {
      DATA_LABEL_TYPE: "seasonalityPickersDataLabelsType",
      KEY: "seasonalityPickersSpecificKey",
      LABELS: "seasonalityPickersContractResolverLabels",
      SEPARATOR: "seasonalityPickersContractResolverSeparator"
    },
    INPUT_PARAMS: {
      DATA_LABEL_TYPE: "inputParamsDataLabelsType",
      LABELS: "inputParamsContractResolverLabels",
      SEPARATOR: "inputParamsContractResolverSeparator"
    }
  }

  const DAYS_ZOOM_PARAM = "seasonalityDefaultZoomValue"
  const DAYS_ZOOM_MIN = 1
  const DAYS_ZOOM_MAX = 500
  const EXPIRY_DATE_FORMAT = "YYYY-MM-DD"
  const EXPIRY_DATE_PARAM = "selectedExpiryDate"
  const EXPIRY_DATE_REFERENCE_LINE_KEY = "expiryDateReferenceLine"
  const SPREAD_BAND_KEY = "spreadBand"

  const SPREAD_BAND_RANGE = {
    MIN: {
      label: "Min",
      value: 0
    },
    PERC_10: {
      label: "10 percentile",
      value: .1
    },
    PERC_25: {
      label: "25 percentile",
      value: .25
    },
    PERC_50: {
      label: "50 percentile",
      value: .5
    },
    PERC_75: {
      label: "75 percentile",
      value: .75
    },
    PERC_90: {
      label: "90 percentile",
      value: .9
    },
    MAX: {
      label: "Max",
      value: 1
    }
  }

  const SPREAD_BAND_USER_SELECTION = {
    USE_SELECTED_YEARS: {
      value: "USE_SELECTED_YEARS",
      label: "Selected years"
    },
    ALL_YEARS: {
      value: "ALL_YEARS",
      label: "All years"
    },
  }

  const BAND_BASED_ON = {
    CHART_DATA: {
      label: "Chart data",
      value: "CHART_DATA"
    },
    FETCH_DATA: {
      label: "Fetch data",
      // Retain the legacy persisted value so existing chart configurations continue to load.
      value: "NORMALIZATION_DATA"
    }
  }

  const SPREAD_LOWER_BAND_KEY = "spreadLowerBand"
  const SPREAD_MEDIAN_KEY = "spreadMedian"
  const SPREAD_UPPER_BAND_KEY = "spreadUpperBand"

  const REFERENCE_BAND_DEFAULTS = {
    bandBasedOn: BAND_BASED_ON.CHART_DATA.value,
    bandExcludedYears: [],
    bandMax: SPREAD_BAND_RANGE.PERC_90.value,
    bandMin: SPREAD_BAND_RANGE.PERC_10.value,
    bandUserSelection: SPREAD_BAND_USER_SELECTION.USE_SELECTED_YEARS.value
  }

  const REFERENCE_DATA_TYPES = {
    BAND: {
      label: "Band",
      value: "BAND"
    },
    LINE: {
      label: "Line",
      value: "LINE"
    }
  }

  const REFERENCE_LINE_AXES = {
    X: {
      label: "X axis",
      value: "X"
    },
    LEFT: {
      label: "Left Y axis",
      value: "LEFT"
    },
    RIGHT: {
      label: "Right Y axis",
      value: "RIGHT"
    }
  }

  // How an X reference line finds its row: a calendar date, an expiry seasonality offset or a calendar seasonality day.
  const REFERENCE_X_AXIS_KINDS = {
    CALENDAR: "CALENDAR",
    DATE: "DATE",
    EXPIRY: "EXPIRY"
  }

  const REFERENCE_X_VALUES = {
    EXPIRY_DATE: {
      label: "Expiry date",
      value: "EXPIRY_DATE"
    },
    TODAY: {
      label: "Today",
      value: "TODAY"
    },
    SPECIFIC_DATE: {
      label: "Specific date",
      value: "SPECIFIC_DATE"
    }
  }

  const REFERENCE_Y_VALUE_TYPES = {
    CONSTANT: "CONSTANT",
    PERCENTILE: "PERCENTILE"
  }

  const REFERENCE_LINE_KEY_PREFIX = "referenceLine_"
  const REFERENCE_BAND_LABELS = {
    area: "Band",
    lower: "Lower band",
    upper: "Upper band"
  }
  const REFERENCE_BAND_KEY_PREFIX = "referenceBand_"

  const AXIS_POSITION = {
    LEFT: "left",
    RIGHT: "right"
  }
  
  const getAxisSelectionValue = axis => axis?.selectionValue || axis?.value || axis?.label

  const ROW_CONFIGS = {
    HEADER: "Header",
    EVEN: "Row 1",
    ODD: "Row 2",
    FOOTER: "Footer"
  }

  const getEmptyRowConfig = rowId => ({
    rowId,
    textAlign: undefined,
    textColor: undefined,
    textStyle: undefined,
    bgColor: undefined,
    fontSize: undefined,
  })

  const ROUNDING_POLICY = {
    HALF_UP: "halfExpand",
    UP: "expand",
    CEILING: "ceil",
    DOWN: "trunc",
    FLOOR: "floor",
  }

  const ROUNDING_POLICIES = [
    {
      label: "Half up",
      value: ROUNDING_POLICY.HALF_UP,
      tooltip: "Rounding mode to round towards 'nearest neighbor' unless both neighbors are equidistant, in which case round up. Behaves as for UP if the discarded fraction is ≥ 0.5; otherwise, behaves as for DOWN. Note that this is the rounding mode commonly taught at school. Examples: 5.5 -> 6, 1.1 -> 1, -2.5 -> -3",
      tooltipPosition: "left"
    },
    {
      label: "Up",
      value: ROUNDING_POLICY.UP,
      tooltip: "Rounding mode to round away from zero. Always increments the digit prior to a non-zero discarded fraction. Note that this rounding mode never decreases the magnitude of the calculated value. Examples: 5.5 -> 6, 1.1 -> 2, -2.5 -> -3",
      tooltipPosition: "left"
    }, 
    {
      label: "Ceiling",
      value: ROUNDING_POLICY.CEILING,
      tooltip: "Rounding mode to round towards positive infinity. If the result is positive, behaves as for UP; if negative, behaves as for DOWN. Note that this rounding mode never decreases the calculated value. Examples: 5.5 -> 6, 1.1 -> 2, -2.5 -> -2",
      tooltipPosition: "left"
    }, 
    {
      label: "Down",
      value: ROUNDING_POLICY.DOWN,
      tooltip: "Rounding mode to round towards zero. Never increments the digit prior to a discarded fraction (i.e., truncates). Note that this rounding mode never increases the magnitude of the calculated value. Examples: 5.5 -> 5, 1.1 -> 1, -2.5 -> -2",
      tooltipPosition: "left"
    }, 
    {
      label: "Floor",
      value: ROUNDING_POLICY.FLOOR,
      tooltip: "Rounding mode to round towards negative infinity. If the result is positive, behave as for DOWN; if negative, behave as for UP. Note that this rounding mode never increases the calculated value. Examples: 5.5 -> 5, 1.1 -> 1, -2.5 -> -3",
      tooltipPosition: "left"
    }, 
  ]

  const COLUMN_TYPES = {
    TEXT: "text",
    DROPDOWN: "dropdown",
    DYNAMIC: "async",
    QUERY_DROPDOWN: "querydropdown"
  }

  const COLUMN_TYPES_ARRAY = [{
    label: "Text",
    value: COLUMN_TYPES.TEXT,
    isReadOnly: true
  }, {
    label: "Dropdown",
    value: COLUMN_TYPES.DROPDOWN
  }, {
    label: "Dynamic",
    value: COLUMN_TYPES.DYNAMIC,
    isReadOnly: true
  }]

  const READ_ONLY_COLUMN_TYPES_ARRAY = COLUMN_TYPES_ARRAY.filter(c => c.isReadOnly)

  const MAP_POSITIONS = {
    AUSTRALIA: {
      label: "Australia",
      value: {
        center: [-27.37, 133.33],
        zoom: 5
      }
    },
    USA: {
      label: "USA",
      value: {
        center: [37.850538913903456, -96.39284133911133],
        zoom: 5
      }
    },
    WORLD: {
      label: "World",
      value: {
        center: [0, 0],
        zoom: 3
      }
    }
  }

  const DIAGRAM_NODES = {
    PIPELINE: {
      type: "Pipeline"
    }
  }

  const AVAILABLE_ACTIONS = {
    PREVIEW: "Preview"
  }

  const CONNECTION_TYPES = {
    ACTION: "Action"
  }

  const DEFAULT_SEASONALITY_EXPIRY_DATE = dayjs.utc().endOf("year").format("YYYY-MM-DD")
  const HORIZONTAL_ALIGNS = [{
    label: "Left",
    value: "left"
  }, {
    label: "Center",
    value: "center"
  }, {
    label: "Right",
    value: "right"
  }]

  const LINE_STYLES = {
    SOLID: {
      value: "SOLID",
      label: "Solid"
    },
    DASHED: {
      value: "DASHED",
      label: "Dashed"
    }
  }

  const LABEL_BASED_ON = {
    DEFAULT: "Default",
    KEY: "Key",
    KEYS_COMBINATION: "Keys combination",
    COLUMN: "Column"
  }

  const HORIZONTAL_ALIGNS_OBJECT = {
    LEFT: "left",
    CENTER: "center",
    RIGHT: "right"
  }

  const INPUT_PARAMS_FIXED_OPTIONS_TYPES = {
    MANUAL: "Manual",
    FILE: "From file"
  }

  const DATALAKE_FILE_HANDLER = {
    FROM_TILE: {
      label: "File from tile",
      value: "FROM_TILE"
    },
    FILE_CREATOR: {
      label: "File creator",
      value: "FILE_CREATOR"
    },
    FILE_PICKER: {
      label: "File picker",
      value: "FILE_PICKER"
    },
    BOTH: {
      label: "Both",
      value: "BOTH"
    }
  }

  const SEASON_MONTH = {
    FIRST: {
      label: "First",
      value: "FIRST"
    },
    LAST: {
      label: "Last",
      value: "LAST"
    }
  }

  const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
  const MONTHS = Array.from(new Array(12), ((el, index) => ({ value: String(index + 1), label: MONTH_NAMES[index] })))

  const CURRENT_MONTH = "CURRENT_MONTH"
  const FIRST_RECORDED_DATE = "FIRST_RECORDED_DATE"
  const LAST_RECORDED_DATE = "LAST_RECORDED_DATE"

  const SEASON_MONTHS_OPTIONS = [{
    label: "Current month",
    value: CURRENT_MONTH
  }, {
    label: "First recorded date",
    value: FIRST_RECORDED_DATE
  }, {
    label: "Last recorded date",
    value: LAST_RECORDED_DATE
  }].concat(MONTHS)

  const AXIS_TYPES = {
    NUMBER: {
      label: "Numbers",
      value: "number"
    }, 
    CATEGORY: {
      label: "Categories",
      value: "category"
    }
  }

  const NORMALIZATION_METHODS = [{
    label: "Min/Max percentage",
    value: "MIN_MAX_PERCENTAGE"
  }]

  const ON_TOO_SMALL_PALETTE_OPTIONS = {
    RANDOM_COLOR: {
      label: "Generate colors",
      value: "RANDOM_COLORS"
    }, 
    REPEAT_PALETTE: {
      label: "Repeat colors",
      value: "REPEAT_PALETTE"
    }
  }

  const CHART_LAYOUT = {
    HORIZONTAL: {
      label: "Horizontal",
      value: "horizontal"
    },
    VERTICAL: {
      label: "Vertical",
      value: "vertical"
    }
  }

  const CHART_LAYOUTS = [
    CHART_LAYOUT.HORIZONTAL,
    CHART_LAYOUT.VERTICAL
  ]

  const LABEL_POSITIONS = [{
    label: "Top",
    value: "top"
  }, {
    label: "Left",
    value: "left"
  }, {
    label: "Right",
    value: "right"
  }, {
    label: "Bottom",
    value: "bottom"
  }, {
    label: "Inside",
    value: "inside"
  }, {
    label: "Outside",
    value: "outside"
  }, {
    label: "End",
    value: "end"
  }, {
    label: "Center",
    value: "center"
  }, {
    label: "Inside: Left",
    value: "insideLeft"
  }, {
    label: "Inside: Right",
    value: "insideRight"
  }, {
    label: "Inside: Top",
    value: "insideTop"
  }, {
    label: "Inside: Bottom",
    value: "insideBottom"
  }, {
    label: "Inside: Top-Left",
    value: "insideTopLeft"
  }, {
    label: "Inside: Bottom-Left",
    value: "insideBottomLeft"
  }, {
    label: "Inside: Top-Right",
    value: "insideTopRight"
  }, {
    label: "Inside: Bottom-Right",
    value: "insideBottomRight"
  }, {
    label: "Inside: Start",
    value: "insideStart"
  }, {
    label: "Inside: End",
    value: "insideEnd"
  }]

  const DOMAIN = {
    DEFAULT: undefined,
    FIT_TO_DATA: "FIT_TO_DATA",
    ZERO_TO_DATA_MAX: "ZERO_TO_DATA_MAX",
    ZERO_TO_100: "ZERO_TO_100",
    AUTO: "AUTO",
    CUSTOM: "CUSTOM"
  }

  const DOMAINS = [{
    value: DOMAIN.DEFAULT,
    formula: undefined,
    label: "Default"
  }, {
    value: DOMAIN.FIT_TO_DATA,
    formula: ['dataMin', 'dataMax'],
    label: "Fit to data"
  }, {
    value: DOMAIN.ZERO_TO_DATA_MAX,
    formula: [0, 'dataMax'],
    label: "Zero to data max"
  }, {
    value: DOMAIN.ZERO_TO_100,
    formula: [0, 100],
    label: "Zero to 100"
  }, {
    value: DOMAIN.AUTO,
    formula: ["auto", "auto"],
    label: "Auto scale"
  }, {
    value: DOMAIN.CUSTOM,
    formula: [],
    label: "Custom"
  }]

  const FORMAT_DOMAIN_OPERATIONS = [{
    label: "Multiply",
    value: "*"
  }, {
    label: "Divide",
    value: "/"
  }]
  const UNITS = [{
    value: "$",
    label: "$"
  }, {
    value: "%",
    label: "%"
  }]

  const MAP_TYPES = [
    {
      label: "Light grey",
      value: "BaseGrayLayer"
    },
    {
      label: "Dark grey",
      value: "BaseDarkGrayLayer"
    },
    {
      label: "Satellite",
      value: "BaseSatelliteLayer"
    },
    {
      label: "Street",
      value: "BaseStreetLayer"
    },
    {
      label: "NatGeo",
      value: "BaseNatGeoLayer"
    }
  ]

  const INIT_FILE_INFO = {
    fileName: "New Insight",
    fileType: "INSIGHT",
    fields: [
        {
            name: "Origin",
            value: "ETL"
        },
        {
            name: "description",
            value: "Save Insight to change it's title, description and banner."
        }
    ],
  }

  const INIT_CONTENT = {
    type: InsightEnums.INSIGHT_TYPE.CHART,
    tiles: [],
    headerBoxes: [],
    mapConfig: {
      mapType: MAP_TYPES[0].value,
      mapPosition: MAP_POSITIONS.WOLRD?.value,
      dataprep: undefined,
      defaultSliderValue: "LATEST"
    },
    mapData: [],
    summaryConfig: {
      column: undefined,
      symbolList: [],
      unit: undefined,
      unitOnFront: false,
      title: "Summary Title",
      subTitle: "Additional information",
      change: "value",
      changeDescription: " from prior",
      withChart: false,
      chartColor: "#addaff",
      chartData: {
        leftAxis: {
          unit: undefined,
          data: [],
          domain: undefined,
          domainMin: undefined,
          domainMax: undefined,
          unitOnFront: false
        },
        rightAxis: {
          unit: undefined,
          data: [],
          domain: undefined,
          domainMin: undefined,
          domainMax: undefined,
          unitOnFront: false
        }
      }
    }
  }

  const ACTIVE_PANEL = {
    ADD_NODES: "ADD_NODES",
    ADD_TILE: "ADD_TILE",
    DRAFTS: "DRAFTS",
    EXPORT_INSIGHT: "EXPORT_INSIGHT",
    NODE_DETAILS: "NODE_DETAILS",
    TILE_EDIT: "TILE_EDIT",
    TILES: "TILES",
    VERSION_HISTORY: "VERSION_HISTORY",
  }

  const TILE_NAME_REGEXP = /[^a-zA-Z0-9()\-_ ]/g 

  const TILE_TYPES = {
    TEXT: "Text",
    CHART: "Chart",
    TABLE: "Table",
    CODE: "CODE",
    /**@deprecated */
    FTL: "FTL",
    PDF: "PDF",
    IMAGE: "IMAGE",
    AUTO_PIPELINE: "AUTO_PIPELINE",
    STATUS: "STATUS",
    DIAGRAM: "DIAGRAM",
    HEADER: "HEADER",
    FOOTER: "FOOTER",
    MAP: "MAP",
    INPUT_PARAMS: "INPUT_PARAMS",
    SEARCH: "SEARCH",
    STATS: "STATS"
  }

  const ACTION_TYPES = {
    REFRESH: "Refresh",
    EXECUTE_PIPELINE: "Execute pipeline",
    OPEN_TILE: "Open tile",
    SAVE_FILE: "Save file",
    SAVE_MARKDOWN: "Save markdown",
    SAVE_HTML: "Save html",
    SAVE_IMAGE: "Save image"
  }

  const TILE_SOURCE_TYPES = {
    FILE: "FILE",
    DATA_PREP: "DATA_PREP",
    SOURCE_TILE: "SOURCE_TILE",
    EDITOR: "EDITOR",
    SQL_CREATOR: "SQL_CREATOR",
  }

  const CODE_TILE_SAVE_FILE_TYPES = {
    FTL: {
      label: "FTL",
      value: ".ftl"
    },
    HTML: {
      label: "HTML",
      value: ".html"
    },
    PDF: {
      label: "PDF",
      value: ".pdf"
    },
  }

  const TABLE_CONTEXT_MENU_ACTIONS = {
    SHOW_COLUMN: {
      label: "Show column",
      value: "SHOW_COLUMN"
    },
    HIDE_COLUMN: {
      label: "Hide column",
      value: "HIDE_COLUMN"
    },
    PREVIEW_DETAILS: {
      label: "Preview details",
      value: "PREVIEW_DETAILS"
    },
    PREVIEW_FILE: {
      label: "File preview",
      value: "PREVIEW_FILE"
    },
    PREVIEW_FORMULA: {
      label: "Preview formula",
      value: "PREVIEW_FORMULA"
    }
  }

  const TABLE_CONTEXT_MENU_PREVIEW_FILE_TYPE = {
    STATIC: {
      label: "Static",
      value: "static"
    },
    DYNAMIC: {
      label: "Dynamic",
      value: "dynamic"
    }
  }

  const TABLE_CONTEXT_MENU_ACTIONS_PLACEMENT_OPTIONS = {
    ENTIRE_TABLE: {
      label: "Entire table",
      value: "ENTIRE_TABLE"
    },
    SELECTED_COLUMNS: {
      label: "Selected columns",
      value: "SELECTED_COLUMNS"
    },
    REGEXP: {
      label: "RegExp",
      value: "REGEXP"
    }
  }

  const TARGET_COLUMN_OPTIONS = {
    LEFT: {
      value: "LEFT",
      label: "Left of selected column"
    },
    RIGHT: {
      value: "RIGHT",
      label: "Right of selected column"
    },
    REGEXP: {
      value: "REGEXP",
      label: "Columns matching RegExp"
    }
  }


  const CHART_INTERPOLATIONS = {
    BASIS: {
      label: 'Basis',
      value: 'basis'
    },
    BASIS_CLOSED: {
      label: 'Basis Closed',
      value: 'basisClosed'
    },  
    BASIS_OPENED: {
      label: 'Basis Open',
      value: 'basisOpen',
    },
    BUMP_X: {
      label: 'Bump X',
      value: 'bumpX'
    },
    BUMP_Y: {
      label: 'Bump Y',
      value: 'bumpY'
    },
    BUMP: {
      label: 'Bump',
      value: 'bump',
    },
    LINEAR: {
      label: 'Linear',
      value: 'linear'
    },
    LINEAR_CLOSED: {
      label: 'Linear Closed',
      value: 'linearClosed'
    },
    NATURAL: {
      label: 'Natural',
      value: 'natural'
    },
    MONOTONE_X: {
      label: 'Monotone X',
      value: 'monotoneX'
    },
    MONOTONE_Y: {
      label: 'Monotone Y',
      value: 'monotoneY',
    },
    MONOTONE: {
      label: 'Monotone',
      value: 'monotone'
    },
    STEP: {
      label: 'Step',
      value: 'step'
    }, 
    STEP_BEFORE: {
      label: 'Step Before',
      value: 'stepBefore'
    },
    STEP_AFTER: {
      label: 'Step After',
      value: 'stepAfter'
    }
  }

  const BUTTON_TYPES = {
    DOWNLOAD_DATA_PREP: "Download data prep",
    DOWNLOAD_FILE: "Download file"
  }

  const AUTO_PIPELINE_RESULTS_ACTION = {
    OPEN_SOURCE_PIPELINE: "OPEN_SOURCE_PIPELINE",
    OPEN_INSIGHT: "OPEN_INSIGHT",
  }

  const AUTO_PIPELINE_RESULTS_ACTIONS = [{
    label: "Open source pipeline",
    value: AUTO_PIPELINE_RESULTS_ACTION.OPEN_SOURCE_PIPELINE
  }, {
    label: "Open insight",
    value: AUTO_PIPELINE_RESULTS_ACTION.OPEN_INSIGHT
  }]

  const CONFIGS = {
    CONDITION_CHART_CONFING: "CONDITION_CHART_CONFING",
    TYPE_CHART_CONFIG: "TYPE_CHART_CONFIG",
    STYLE_CHART_CONFIG: "STYLE_CHART_CONFIG",
    TOOLTIP_CHART_CONFIG: "TOOLTIP_CHART_CONFIG",
    DATA_SOURCE_CHART_CONFIG: "DATA_SOURCE_CHART_CONFIG",
    ADVANCED_CHART_CONFIG: "ADVANCED_CHART_CONFIG",
    CURVE_CHART_CONFIG: "CURVE_CHART_CONFIG",
    BASIC_CHART_CONFIG: "BASIC_CHART_CONFIG",
    CHART_X_AXIS: "CHART_X_AXIS_CONFIG",
    DATA_LABELS: "DATA_LABELS",
    CHART_Y_AXIS: "CHART_Y_AXIS_CONFIG",
    PICKERS_CONFIG: "PICKERS_CONFIG",
    TABLE_SOURCE_FILE_CONFIG: "TABLE_SOURCE_FILE_CONFIG",
    TABLE_ADVANCED_CONFIG: "TABLE_ADVANCED_CONFIG",
    TABLE_ADVANCED_SORT_CONFIG: "TABLE_ADVANCED_SORT_CONFIG",
    TABLE_FORMULA_PANEL_CONFIG: "TABLE_FORMULA_PANEL_CONFIG",
    TABLE_FORMULA_PANEL_METADATA_KEY: "TABLE_FORMULA_PANEL_METADATA_KEY",
    TABLE_ADVANCED_CUSTOM_ACTION: "TABLE_ADVANCED_CUSTOM_ACTION",
    TABLE_STYLE_CONFIG: "TABLE_STYLE_CONFIG",
    TEXT_STYLE_CONFIG: "TEXT_STYLE_CONFIG",
    TEXT_SOURCE_CONFIG: "TEXT_SOURCE_CONFIG",
    IMAGE_SOURCE_CONFIG: "IMAGE_SOURCE_CONFIG",
    IMAGE_STYLE_CONFIG: "IMAGE_STYLE_CONFIG",
    PDF_SOURCE_CONFIG: "PDF_SOURCE_CONFIG",
    PDF_STYLE_CONFIG: "PDF_STYLE_CONFIG",
    ACTION: "ACTION_CONFIG_",
    KEY_SELECTION: "KEY_SELECTION_CONFIG_",
    PARTNER: "PARTNER_CONFIG_",
    DATA_SET: "DATA_SET_",
    COLUMN: "TABLE_COLUMN_",
    CONDITIONAL_FORMATTING: "CONDITIONAL_FORMATTING_",
    REFERENCE_DATA: "REFERENCE_DATA_",

    TABLE_CUSTOMIZATION: 'TABLE_CUSTOMIZATION',

    FOOTER_BASIC_CONFIG: "FOOTER_BASIC_CONFIG",
    FOOTER_BUTTONS: "FOOTER_BUTTONS",
    FOOTER_SINGLE_BUTTON: "FOOTER_SINGLE_BUTTON_",

    HEADER_BASIC_CONFIG: "HEADER_BASIC_CONFIG",
    HEADER_BOX: "HEADER_BOX_",

    AUTO_PIPELINE_SOURCE_CONFIG: "AUTO_PIPELINE_SOURCE_CONFIG",
    AUTO_PIPELINE_STYLE_CONFIG: "AUTO_PIPELINE_STYLE_CONFIG",
    AUTO_PIPELINE_CONFIGURATION_CONFIG: "AUTO_PIPELINE_CONFIGURATION_CONFIG",
    AUTO_PIPELINE_PARAMETER: "AUTO_PIPELINE_PARAMETER",
    AUTO_PIPELINE_PARAMETERS: "AUTO_PIPELINE_PARAMETERS",

    STATUS_SOURCE_CONFIG: "STATUS_SOURCE_CONFIG",
    STATUS_CONFIGURATION_CONFIG: "STATUS_CONFIGURATION_CONFIG",
    STATUS_STYLE_CONFIG: "STATUS_STYLE_CONFIG",
    STATUS_FILTERS: "STATUS_FILTERS",
    STATUS_FILTER_ITEM: "STATUS_FILTER_ITEM",
    STATUS_PIPELINE_ITEMS: "STATUS_PIPELINE_ITEMS",
    STATUS_PIPELINE_ITEM: "STATUS_PIPELINE_ITEM",

    CODE_TYPE_CONFIG: "CODE_TYPE_CONFIG",
    CODE_SOURCE_CONFIG: "CODE_SOURCE_CONFIG",
    CODE_STYLE_CONFIG: "CODE_STYLE_CONFIG",
    CODE_PASSED_FILES_CONFIG: "CODE_PASSED_FILES_CONFIG",
    CODE_PASSED_FILE_CONFIG: "CODE_PASSED_FILE_CONFIG",

    DIAGRAM_STYLE_CONFIG: "DIAGRAM_STYLE_CONFIG",

    BASIC_MAP_CONFIG: "BASIC_MAP_CONFIG",
    MAP_SUMMARY: "MAP_SUMMARY",
    MAP_Y_AXIS: "MAP_Y_AXIS",
    MAP_CHART_DATA: "MAP_CHART_DATA",
    MAP_CHART_SUMMARY_DATA: "MAP_CHART_SUMMARY_DATA",
    MAP_Y_AXIS_SUMMARY: "MAP_Y_AXIS_SUMMARY",

    PIPELINE_SOURCE_DIAGRAM_PIPELINE_NODE: "PIPELINE_SOURCE_DIAGRAM_PIPELINE_NODE",
    IMPORT_ATTRIBUTES_DIAGRAM_PIPELINE_NODE: "IMPORT_ATTRIBUTES_DIAGRAM_PIPELINE_NODE",
    ATTRIBUTE_CONFIG_DIAGRAM_PIPELINE_NODE: "ATTRIBUTE_CONFIG_DIAGRAM_PIPELINE_NODE",
    CONNECTION_CONFIG_DIAGRAM_PIPELINE_NODE: "CONNECTION_CONFIG_DIAGRAM_PIPELINE_NODE",
    STYLE_CONFIG_DIAGRAM_PIPELINE_NODE: "STYLE_CONFIG_DIAGRAM_PIPELINE_NODE",

    BASIC_INPUT_PARAMS_CONFIG: "BASIC_INPUT_PARAMS_CONFIG",
    TYPE_INPUT_PARAMS_CONFIG: "TYPE_INPUT_PARAMS_CONFIG",
    SEARCH_TYPE_CONFIG: "SEARCH_TYPE_CONFIG",
    ADVANCED_INPUT_PARAMS_CONFIG: "ADVANCED_INPUT_PARAMS_CONFIG",
    INPUT_PARAMS_KEY: "INPUT_PARAMS_KEY",
    INPUT_PARAMS_PRESET: "INPUT_PARAMS_PRESET",
    INPUT_PARAMS_COLUMN: "INPUT_PARAMS_COLUMN",
    INPUT_PARAMS_VALUE: "INPUT_PARAMS_VALUE",
    INPUT_PARAMS_CURVE: "INPUT_PARAMS_CURVE",
    STYLE_INPUT_PARAMS_CONFIG: "STYLE_INPUT_PARAMS_CONFIG",

    SEARCH_FILTER_ITEM: "SEARCH_FILTER_ITEM",
    SEARCH_SOURCE_CONFIG: "SEARCH_SOURCE_CONFIG",
    SEARCH_CONFIGURATION_CONFIG: "SEARCH_CONFIGURATION_CONFIG",
    SEARCH_STYLE_CONFIG: "SEARCH_STYLE_CONFIG",
  }

  const APPLY_TO_RADIO = {
    DATA: "data",
    KEY: "key",
    COLUMN: "column"
  }

  const EMPTY_COLOR_PALETTE = ["#3d3b3b", "#545353", "#717171", "#a19e9e", "#d2d2d2", "#e6e6e6", "#f9f9f9"]

  const DEFAULT_COLOR_PALETTES = [{
    name: "COLORFUL_2",
    colors: ["#6473fa", "#fbd75c", "#3fa193", "#fa9493", "#98bbfb", "#f8b14f", "#9f66e3"]
  }, {
    name: "BLUE",
    colors: ["#101640", "#022872", "#1262b6", "#2387da", "#8acaff", "#addaff", "#ecf8ff"]
  }, {
    name: "COLORFUL_1",
    colors: ["#b6cc80", "#f39672", "#77c5e1", "#f4d290", "#d8a3e0", "#bebebe", "#6ca3bc"]
  }, {
    name: "GREEN",
    colors: ["#002700", "#014306", "#1b5e20", "#4c8c4a", "#81c784", "#a5d6a7", "#c8e6c9"]
  }, {
    name: "RED",
    colors: ["#8f1414", "#a81d1d", "#db2626", "#d95e2c", "#e98519", "#efa644", "#f3c585"]
  }]

  const DEFAULT_CHART_COLOR_PALETTE = DEFAULT_COLOR_PALETTES[0]
  const DEFAULT_MAP_COLOR_PALETTE = DEFAULT_COLOR_PALETTES[3]
  const DEFAULT_AREA_DIFF_COLOR_SCALE = [
    { color: "#EDFDED", value: 0 },
    { color: "#DAFBDB", value: 5 },
    { color: "#C6F1C7", value: 10 },
    { color: "#ADEBAF", value: 15 },
    { color: "#95E497", value: 20 },
    { color: "#FFEBEB", value: -5 },
    { color: "#FFD6D6", value: -10 },
    { color: "#F8BFBF", value: -15 },
    { color: "#F0A8A8", value: -20 },
    { color: "#F49A9A", value: -25 }
  ]

  const TILE_HEIGHT = {
    FULL: "100%",
    HALF: "50%",
    QUARTER: "25%"
  }

  const TILE_WIDTH = {
    FULL: "100%",
    HALF: "50%"
  }

  const CONFIGURATION_SECTIONS = {
    ADVANCED: {
      value: "ADVANCED",
      label: "Advanced configuration"
    },
    BASIC_CONFIGURATION: {
      value: "BASIC_CONFIGURATION",
      label: "Basic configuration"
    },
    CONFIGURATION: {
      value: "CONFIGURATION",
      label: "Configuration"
    },
    CURVE_CONFIGURATION: {
      value: "CURVE_CONFIGURATION",
      label: "Curve configuration"
    },
    CUSTOMIZATION: {
      value: "CUSTOMIZATION",
      label: "Customization"
    },
    DATA_LABELS: {
      value: "DATA_LABELS",
      label: "Data labels"
    },
    PASSED_FILES: {
      value: "PASSED_FILES",
      label: "Passed files"
    },
    SOURCE: {
      value: "SOURCE",
      label: "Source"
    },
    STYLE: {
      value: "STYLE",
      label: "Style"
    },
    X_AXIS: {
      value: "X_AXIS",
      label: "X Axis"
    },
    Y_AXIS: {
      value: "Y_AXIS",
      label: "Y Axis"
    },
    PICKERS: {
      value: "PICKERS",
      label: "Pickers"
    }
  }

  const EMPTY_FOOTER_BUTTON = {
    dataprep: {
      defaultValue: undefined,
      exportable: true,
      label: "Data prep"
    },
    file: {
      defaultValue: undefined,
      exportable: true,
      label: "File"
    },
    text: {
      defaultValue: undefined,
      exportable: true,
      label: "Text"
    },
    type: {
      defaultValue: undefined,
      exportable: true,
      label: "Type"
    }
  }

  const EMPTY_FOOTER = {
    buttons: {
      defaultValue: [],
      exportable: true,
      label: "Buttons"
    },
    text: {
      defaultValue: undefined,
      exportable: true,
      label: "Text",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    }
  }

  const STATUS_FILTER_TYPES = {
    GROUP_NAME: "Group",
    QUERY: "Search term",
    STATUS: "Status",
    TAG: "Tag",
  }

  const STATUS_MAX_LIMIT = 200

  const STATUS_SELECTED_BY = {
    FILTER: "Filter",
    ITEMS: "Items"
  }

  const EMPTY_HEADER_BOX = {
    title: {
      defaultValue: "",
      exportable: true,
      label: ""
    },
    withTooltip: {
      defaultValue: false,
      exportable: true,
      label: "Tooltip"
    },
    tooltipText: {
      defaultValue: "",
      exportable: true,
      label: "Tooltip text"
    },
    tooltipUrl: {
      defaultValue: "",
      exportable: true,
      label: "Tooltip url"
    },
    symbol: {
      defaultValue: undefined,
      exportable: true,
      label: "Symbol"
    },
    column: {
      defaultValue: undefined,
      exportable: true,
      label: "Column"
    },
    withChange: {
      defaultValue: true,
      exportable: true,
      label: "Display value change"
    },
    withPercentChange: {
      defaultValue: false,
      exportable: true,
      label: "Display percent change"
    },
    unit: {
      defaultValue: undefined,
      exportable: true,
      label: "Data unit:"
    },
    unitOnFront: {
      defaultValue: false,
      exportable: true,
      label: "↳ Display unit:"
    },
    dateFormat: {
      defaultValue: undefined,
      exportable: true,
      label: "Date format"
    }
  }

  const TITLE_TOOLTIP_DEFAULT = {
    titleTooltip: {
      defaultValue: false,
      exportable: true,
      label: "Info tooltip",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
    titleTooltipText: {
      defaultValue: "",
      exportable: true,
      label: "Title tooltip text",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
    titleTooltipUrl: {
      defaultValue: "",
      exportable: true,
      label: "Title tooltip url",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
  }

  const CHART_OUTPUT_CONFIG_DEFAULT = {
    legendSize: {
      defaultValue: undefined,
      exportable: true,
      label: "Legend size",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
    titleSize: {
      defaultValue: undefined,
      exportable: true,
      label: "Title size",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
    imageHeight: {
      defaultValue: undefined,
      exportable: true,
      label: "Image height",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
    imageWidth: {
      defaultValue: undefined,
      exportable: true,
      label: "Image width",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
  }

  const EMPTY_CONDITION_FORMATTING_CURVE_ITEM = {
    applyTo: {
      defaultValue: undefined,
      exportable: true,
      label: "Apply to:"
    },
    applyToIndex: {
      defaultValue: "5",
      exportable: true,
      label: "Index"
    },
    color: {
      defaultValue: undefined,
      exportable: true,
      label: "Color"
    },
    label: {
      defaultValue: undefined,
      exportable: true,
      label: "Label"
    },
    chartType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type:"
    },
    conditionalColorPalette: {
      defaultValue: undefined,
      exportable: true,
      label: "Color"
    },
    colorScale: {
      defaultValue: undefined,
      exportable: true,
      label: "Color scale"
    },
    dotSize: {
      defaultValue: "5",
      exportable: true,
      label: "Dot size"
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden"
    },
    showInLegend: {
      defaultValue: true,
      exportable: true,
      label: "Show in legend"
    },
    shouldFormatRow: {
      defaultValue: false,
      exportable: true,
      label: "Format whole row"
    },
  }

  const EMPTY_CONDITION_FORMATTING_AREA_DIFF_ITEM = {
    applyTo: {
      defaultValue: undefined,
      exportable: true,
      label: "Apply to:"
    },
    applyToAxis: {
      defaultValue: undefined,
      exportable: true,
      label: "Apply to"
    },
    applyToRadio: {
      defaultValue: APPLY_TO_RADIO.DATA,
      exportable: true,
      label: "Apply to:"
    },
    axis: {
      defaultValue: undefined,
      exportable: true,
      label: "Axis:"
    },
    colorScale: {
      defaultValue: DEFAULT_AREA_DIFF_COLOR_SCALE,
      exportable: true,
      label: "Color scale"
    },
    conditionalColorPalette: {
      defaultValue: undefined,
      exportable: true,
      label: "Color"
    },
    chartType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type:"
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden"
    },
    label: {
      defaultValue: undefined,
      exportable: true,
      label: "Label"
    },
    showInLegend: {
      defaultValue: false,
      exportable: true,
      label: "Show in legend"
    }
  }

  const EMPTY_REFERENCE_DATA_ITEM = {
    bandBasedOn: {
      defaultValue: undefined,
      exportable: true,
      label: "Band based on",
      mapper: value => Object.values(BAND_BASED_ON).find(option => option.value === value)
    },
    bandExcludedYears: {
      defaultValue: undefined,
      exportable: true,
      label: "Excluded calculation years"
    },
    bandMax: {
      defaultValue: undefined,
      exportable: true,
      label: "Upper band",
      mapper: value => Object.values(SPREAD_BAND_RANGE).find(m => m.value === value)
    },
    bandMin: {
      defaultValue: undefined,
      exportable: true,
      label: "Lower band",
      mapper: value => Object.values(SPREAD_BAND_RANGE).find(m => m.value === value)
    },
    bandUserSelection: {
      defaultValue: undefined,
      exportable: true,
      label: "Calculate band based on",
      mapper: value => Object.values(SPREAD_BAND_USER_SELECTION).find(option => option.value === value)
    },
    referenceDataType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type"
    },
    referenceLineAxis: {
      defaultValue: undefined,
      exportable: true,
      label: "Axis"
    },
    referenceLineDate: {
      defaultValue: undefined,
      exportable: true,
      label: "Date"
    },
    referenceLineXValue: {
      defaultValue: undefined,
      exportable: true,
      label: "Value"
    },
    referenceLineYValue: {
      defaultValue: undefined,
      exportable: true,
      label: "Value"
    }
  }

  const EMPTY_CONDITION_FORMATTING_ITEM = {
    applyTo: {
      defaultValue: undefined,
      exportable: true,
      label: "Apply to:"
    },
    applyToAxis: {
      defaultValue: undefined,
      exportable: true,
      label: "Apply to"
    },
    applyToRadio: {
      defaultValue: APPLY_TO_RADIO.DATA,
      exportable: true,
      label: "Apply to:"
    },
    axis: {
      defaultValue: AXIS_POSITION.LEFT,
      exportable: true,
      label: "Axis:"
    },
    bgColor: {
      defaultValue: undefined,
      exportable: true,
      label: "Cell color"
    },
    borderColor: {
      defaultValue: "#e6e6e6",
      exportable: true,
      label: "Border color"
    },
    bordersToFormat: {
      defaultValue: undefined,
      exportable: true,
      label: "Cell color"
    },
    borderSize: {
      defaultValue: "1",
      exportable: true,
      label: "Border size"
    },
    chartType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type:"
    },
    color: {
      defaultValue: undefined,
      exportable: true,
      label: "Color"
    },
    columnColorPallete: {
      defaultValue: undefined,
      exportable: true,
      label: "Cell colors"
    },
    columnIndex: {
      defaultValue: undefined,
      exportable: true,
      label: "Column index"
    },
    columnRegexp: {
      defaultValue: undefined,
      exportable: true,
      label: "Column RegExp"
    },
    colspan: {
      defaultValue: "1",
      exportable: true,
      label: "Column span"
    },
    condition: {
      defaultValue: undefined,
      exportable: true,
      label: "Format conditionally"
    },
    conditionColumnToCompare: {
      defaultValue: undefined,
      exportable: true,
      label: "Cell to compare with"
    },
    conditionValueA: {
      defaultValue: undefined,
      exportable: true,
      label: "Condition value"
    },
    conditionValueB: {
      defaultValue: undefined,
      exportable: true,
      label: "Condition value"
    },
    conditionValue: {
      defaultValue: undefined,
      exportable: true,
      label: "Value"
    },
    conditionalColorPalette: {
      defaultValue: undefined,
      exportable: true,
      label: "Color"
    },
    colorScale: {
      defaultValue: undefined,
      exportable: true,
      label: "Color scale"
    },
    dotSize: {
      defaultValue: "5",
      exportable: true,
      label: "Dot size"
    },
    filterZero: {
      defaultValue: false,
      exportable: true,
      label: "Filter zero",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
    },
    fontSize: {
      defaultValue: undefined, 
      exportable: true,
      label: "Font size",
      mapper: value => value === "11" ? "Small" : value === "12" ? "Medium" : value === "16" ? "Large" : value
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden"
    },
    label: {
      defaultValue: undefined,
      exportable: true,
      label: "Label"
    },
    labelAngle: {
      defaultValue: "0",
      exportable: true,
      label: "Label angle"
    },
    labelPosition: {
      defaultValue: LABEL_POSITIONS.find(({ value }) => value === "center")?.value,
      exportable: true,
      label: "Label position"
    },
    onTooSmallPalette: {
      defaultValue: ON_TOO_SMALL_PALETTE_OPTIONS.RANDOM_COLOR.value,
      exportable: true,
      label: "If more colors are needed:"
    },
    rowIndex: {
      defaultValue: undefined,
      exportable: true,
      label: "Row index"
    },
    rowspan: {
      defaultValue: "1",
      exportable: true,
      label: "Row span"
    },
    showInLegend: {
      defaultValue: true,
      exportable: true,
      label: "Show in legend"
    },
    shouldFormatBorders: {
      defaultValue: true,
      exportable: true,
      label: "Format borders"
    },
    shouldFormatHeaderCell: {
      defaultValue: false,
      exportable: true,
      label: "Format header cell"
    },
    shouldFormatRow: {
      defaultValue: false,
      exportable: true,
      label: "Format whole row"
    },
    textAlign: {
      defaultValue: true,
      exportable: true,
      label: "Text align"
    },
    textColor: {
      defaultValue: true,
      exportable: true,
      label: "Text color"
    },
    textStyle: {
      defaultValue: true,
      exportable: true,
      label: "Text style"
    },
    withLabel: {
      defaultValue: false,
      exportable: true,
      label: "Show label"
    },
  }

  const SINGLE_ACTION = {
    actionName: {
      defaultValue: undefined,
      exportable: true,
      label: "Name"
    },
    actionType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type"
    },
    fileName: {
      defaultValue: undefined,
      exportable: true,
      label: "File name"
    },
    fileType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type"
    },
    groupName: {
      defaultValue: undefined,
      exportable: true,
      label: "Group name"
    },
    shouldShowCorrections: {
        defaultValue: false,
        exportable: true,
        label: `Mark NCSV as "Correction"`,
      },
    openTileImageHeight: {
      defaultValue: undefined,
      exportable: true,
      label: "Image height"
    },
    openTileImageWidth: {
      defaultValue: undefined,
      exportable: true,
      label: "Image width"
    },
    pipelineName: {
      defaultValue: undefined,
      exportable: true,
      label: "Pipeline name"
    },
    pipelineGroupName: {
      defaultValue: undefined,
      exportable: true,
      label: "Pipeline group"
    },
    pipelineId: {
      defaultValue: undefined,
      exportable: true,
      label: "Pipeline id"
    },
    saveAs: {
      defaultValue: undefined,
      exportable: true,
      label: "Save as"
    },
    shouldRefreshOnPipelineExecution: {
      defaultValue: undefined,
      exportable: true,
      label: "Refresh tiles after pipeline execution"
    },
    type: {
      defaultValue: undefined,
      exportable: true,
      label: "Type"
    },
    tooltip: {
      defaultValue: undefined,
      exportable: true,
      label: "Hover tooltip"
    }
  }

  const ACTIONS = {
    actions: {
      defaultValue: [],
      exportable: true,
      label: "Actions"
    },
  }

  const EMPTY_PARTNER = {
    imageUrl: {
      defaultValue: undefined,
      exportable: true,
      label: "Logo"
    },
    logoTextUrl: {
      defaultValue: undefined,
      exportable: true,
      label: "Open url on click"
    }
  }

  const PARTNERS = {
    partners: {
      defaultValue: [],
      exportable: true,
      label: "Partners"
    },
  }
  const EMPTY_AUTO_PIPELINE_PARAM = {
    datalakeFileHandlerSourceTile: {
      defaultValue: undefined,
      exportable: true,
      label: "Source tile"
    },
    datalakeFileHandlerSourceTileAction: {
      defaultValue: undefined,
      exportable: true,
      label: "Source tile action"
    },
    defaultValue: {
      defaultValue: undefined,
      exportable: true,
      label: "Default value"
    },
    extraGroupQuery: {
      defaultValue: undefined,
      exportable: true,
      label: "Extra group query"
    },
    taskReadableName: {
      defaultValue: undefined,
      exportable: true,
      label: "Task"
    },
    name: {
      defaultValue: undefined,
      exportable: true,
      label: "Parameter name"
    },
    readableName: {
      defaultValue: undefined,
      exportable: true,
      label: "Parameter readable name"
    },
    optional: {
      defaultValue: undefined,
      exportable: true,
      label: "Optional"
    },
    specificDate: {
      defaultValue: true,
      exportable: true,
      label: "Specific Date"
    },
    includeTime: {
      defaultValue: false,
      exportable: true,
      label: "Include time"
    },
    dateRangeEnd: {
      defaultValue: undefined,
      exportable: true,
      label: "End date parameter"
    },
    label: {
      defaultValue: undefined,
      exportable: true,
      label: "Label"
    },
    valueType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type of value"
    },
    datalakeFileHandler: {
      defaultValue: DATALAKE_FILE_HANDLER.FILE_PICKER.value,
      exportable: true,
      label: "Type"
    },
    fileExtension: {
      defaultValue: undefined,
      exportable: true,
      label: "File extension"
    },
    fileType: {
      defaultValue: undefined,
      exportable: true,
      label: "File type"
    },
    value: {
      defaultValue: undefined,
      exportable: true,
      label: "Value"
    },
    options: {
      defaultValue: undefined,
      exportable: true,
      label: "Options"
    },
    datalakeFileHandlerFileName: {
      defaultValue: undefined,
      exportable: true,
      label: "Uploaded file name"
    },
    datalakeFileHandlerGroupName: {
      defaultValue: undefined,
      exportable: true,
      label: "Uploaded group name"
    },
    min: {
      defaultValue: undefined,
      exportable: true,
      label: "Minimum"
    },
    max: {
      defaultValue: undefined,
      exportable: true,
      label: "Maximum"
    },
    fileWithOptions: {
      defaultValue: undefined,
      exportable: true,
      label: "Options from file"
    },
    dependsOn: {
      defaultValue: undefined,
      exportable: true,
      label: "Depends on"
    },
    expression: {
      defaultValue: undefined,
      exportable: true,
      label: "Expression"
    },
    withProvider: {
      defaultValue: undefined,
      exportable: true,
      label: "With provider"
    },
    shouldSynchronizeWithInputParams: {
      defaultValue: false,
      exportable: true,
      label: "Synchronize with Input Parameter Tool"
    },
    synchronizedInputParameter: {
      defaultValue: undefined,
      exportable: true,
      label: "Input Parameter Tool"
    },
    synchronizedParameter: {
      defaultValue: undefined,
      exportable: true,
      label: "Synchronized parameter"
    },
    disabled: {
      defaultValue: false,
      exportable: true,
      label: "Disabled"
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden"
    },
  }

  const CHART_AXIS_CONFIG = {
    allowSelection: {
      defaultValue: false,
      exportable: true,
      label: "Allow selection",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    axisLabel: {
      defaultValue: undefined,
      exportable: true,
      label: "Label",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    axisType: {
      defaultValue: AXIS_TYPES.NUMBER?.value,
      exportable: true,
      label: "Axis data type",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    connectNulls: {
      defaultValue: true,
      exportable: true,
      label: "Connect missing data",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    colorPalette: {
      defaultValue: undefined,
      exportable: true,
      label: "Color palette",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    data: {
      defaultValue: [],
      exportable: false,
      label: "Data"
    },
    decimalPrecision: {
      defaultValue: "2",
      exportable: true,
      label: "Decimal precision:",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    domain: {
      defaultValue: undefined,
      exportable: true,
      label: "Domain:",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    domainMin: {
      defaultValue: undefined,
      exportable: true,
      label: "Domain min",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    domainMax: {
      defaultValue: undefined,
      exportable: true,
      label: "Domain max",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    formatDomainOperation: {
      defaultValue: undefined,
      exportable: true,
      label: "Format domain operation:",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    formatDomainValue: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Format domain by:",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    formatDomainUnit: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Format domain unit:",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    isNormalizationEnabled: {
      defaultValue: false,
      exportable: true,
      label: "Enable normalization",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    isStackingEnabled: {
      defaultValue: false,
      exportable: true,
      label: "Stack bars / areas",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    normalizationMethod: {
      defaultValue: NORMALIZATION_METHODS[0].value,
      exportable: true,
      label: "Normalization method",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    reverseYAxis: {
      defaultValue: false,
      exportable: true,
      label: "Reverse ticks",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    unit: {
      defaultValue: undefined,
      exportable: true,
      label: "Data unit:",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    unitOnFront: {
      defaultValue: false,
      exportable: true,
      label: "↳ Display unit:",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
    yAxisTickAngle: {
      defaultValue: "0",
      exportable: true,
      label: "Tick angle",
      section: CONFIGURATION_SECTIONS.Y_AXIS.value
    },
  }

  const EMPTY_CHART_DATASET = {
    chartType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type:"
    },
    color: {
      defaultValue: undefined,
      exportable: true,
      label: "Color"
    },
    column: {
      defaultValue: undefined,
      exportable: false,
      label: "Source column"
    },
    columnXAxis: {
      defaultValue: undefined,
      exportable: false,
      label: "X axis"
    },
    columnXAxisTimezone: {
      defaultValue: undefined,
      exportable: false,
      label: "X axis timezone"
    },
    columnXAxisIsNumber: {
      defaultValue: undefined,
      exportable: false,
      label: "X axis is number"
    },
    descendingOpacity: {
      defaultValue: false,
      exportable: true,
      label: "Descending opacity level"
    },
    dotSize: {
      defaultValue: "5",
      exportable: true,
      label: "Dot size"
    },
    dynamic: {
      defaultValue: false,
      exportable: true,
      label: "Dataset type"
    },
    groupName: {
      defaultValue: undefined,
      exportable: false,
      label: "Group"
    },
    isRegression: {
      defaultValue: undefined,
      exportable: false,
      label: "Regression / Trend line"
    },
    isHistogram: {
      defaultValue: false,
      exportable: true,
      label: "Histogram"
    },
    labelAngle: {
      defaultValue: "1",
      exportable: true,
      label: "Label angle"
    },
    labelPosition: {
      defaultValue: LABEL_POSITIONS.find(({ value }) => value === "center")?.value,
      exportable: true,
      label: "Label position"
    },
    lineOpacity: {
      defaultValue: "0",
      exportable: true,
      label: "Line opacity"
    },
    lineStyle: {
      defaultValue: LINE_STYLES.SOLID.value,
      exportable: true,
      label: "Line style"
    },
    lineThickness: {
      defaultValue: "1",
      exportable: true,
      label: "Line thickness"
    },
    movingAverage: {
      defaultValue: false,
      exportable: true,
      label: "Moving average"
    },
    movingAverageValue: {
      defaultValue: undefined,
      exportable: false,
      label: "↳ Moving average value"
    },
    opacityStep: {
      defaultValue: undefined,
      exportable: true,
      label: "Opacity level step"
    },
    operation: {
      defaultValue: undefined,
      exportable: true,
      label: "Operation"
    },
    showInLegend: {
      defaultValue: true,
      exportable: true,
      label: "Show in legend"
    },
    withReferenceLine: {
      defaultValue: false,
      exportable: true,
      label: "Reference line"
    },
    withLabel: {
      defaultValue: false,
      exportable: true,
      label: "Show label"
    },
  }

  const EMPTY_INPUT_PARAM_ITEM = {
    column: {
      defaultValue: [],
      exportable: false,
      label: "Column"
    },
    columnDateFormat: {
      defaultValue: undefined,
      exportable: false,
      label: "Date format"
    },
    columnType: {
      defaultValue: undefined,
      exportable: false,
      label: "Column type"
    },
    defaultValue: {
      defaultValue: undefined,
      exportable: false,
      label: "Default value"
    },
    defaultValueFrom: {
      defaultValue: undefined,
      exportable: false,
      label: "Default from"
    },
    defaultValueTo: {
      defaultValue: undefined,
      exportable: false,
      label: "Default to"
    },
    dropdownOptions: {
      defaultValue: undefined,
      exportable: false,
      label: "Dropdown options"
    },
    fixedOptions: {
      defaultValue: undefined,
      exportable: false,
      label: "Options"
    },
    fixedOptionsFile: {
      defaultValue: undefined,
      exportable: false,
      label: "↳ Options from file"
    },
    fixedOptionsType: {
      defaultValue: INPUT_PARAMS_FIXED_OPTIONS_TYPES.MANUAL,
      exportable: false,
      label: "Type"
    },
    inputParamsDataLabelsType: {
      defaultValue: SEASONALITY_PICKERS_DATA_LABEL_TYPES.DEFAULT.value,
      exportable: true,
      label: "Display labels names based on",
      mapper: value => Object.values(SEASONALITY_PICKERS_DATA_LABEL_TYPES).find(type => type.value === value),
    },
    inputParamsContractResolverLabels: {
      defaultValue: undefined,
      exportable: true,
      label: "Choose labels"
    },
    inputParamsContractResolverSeparator: {
      defaultValue: " / ",
      exportable: true,
      label: "Separator"
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    operator: {
      defaultValue: undefined,
      exportable: true,
      label: "Condition"
    },
    templateContent: {
      defaultValue: undefined,
      exportable: false,
      label: "Template content"
    },
    withAll: {
      defaultValue: false,
      exportable: true,
      label: "With 'All' option"
    },
    withFixedOptions: {
      defaultValue: false,
      exportable: true,
      label: "Fixed options"
    },
    withNonPatternAll: {
      defaultValue: false,
      exportable: true,
      label: "Prefer non-pattern 'Select all' option"
    },
    withSingleSelect: {
      defaultValue: false,
      exportable: false,
      label: "Single select"
    },
  }

  const EMPTY_SYMBOL = {
    availableOptions: {
      defaultValue: undefined,
      exportable: false,
      label: "Available options"
    },
    defaultValue: {
      defaultValue: undefined,
      exportable: false,
      label: "Default value"
    },
    fixedValue: {
      defaultValue: undefined,
      exportable: false,
      label: "Fixed value"
    },
    isRegression: {
      defaultValue: undefined,
      exportable: false,
      label: "Regression / Trend line"
    },
    keyDescription: {
      defaultValue: undefined,
      exportable: false,
      label: "Description"
    },
    movingAverage: {
      defaultValue: undefined,
      exportable: false,
      label: "Moving average"
    },
    movingAverageValue: {
      defaultValue: undefined,
      exportable: false,
      label: "↳ Moving average value"
    },
    symbolKey: {
      defaultValue: undefined,
      exportable: false,
      label: "Key"
    },
    useDescriptionAsLabel: {
      defaultValue: undefined,
      exportable: false,
      label: "Use description as label"
    },
    withDescription: {
      defaultValue: false,
      exportable: false,
      label: "↳ With description"
    },
    isSingle: {
      defaultValue: false,
      exportable: false,
      label: "Single select"
    },
    withSingleSelect: {
      defaultValue: false,
      exportable: false,
      label: "Single select"
    },
  }

  const EMPTY_SEASONALITY_DATA = {
    column: {
      defaultValue: false,
      exportable: false,
      label: "Column"
    },
    filterZero: {
      defaultValue: false,
      exportable: false,
      label: "Filter zero"
    },
  }

  const EMPTY_SEARCH_FILTER = {
    defaultValue: {
      defaultValue: undefined,
      exportable: true,
      label: "Default value"
    },
    filterKey: {
      defaultValue: undefined,
      exportable: true,
      label: "Filter"
    },
    isSingleSelect: {
      defaultValue: true,
      exportable: true,
      label: "Single select"
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden"
    },
    label: {
      defaultValue: undefined,
      exportable: true,
      label: "Label"
    },
    type: {
      defaultValue: undefined,
      exportable: true,
      label: "Type"
    },
  }

  const EMPTY_STATUS_PIPELINE_ITEM = {
    label: {
      defaultValue: undefined,
      exportable: true,
      label: "Label"
    },
    pipeline: {
      defaultValue: undefined,
      exportable: true,
      label: "Pipeline"
    }
  }

  const EMPTY_STATUS_FILTER = {
    groupName: {
      defaultValue: undefined,
      exportable: false,
      label: "Group"
    },
    label: {
      defaultValue: undefined,
      exportable: false,
      label: "Label"
    },
    query: {
      defaultValue: "*",
      exportable: true,
      label: "Search term"
    },
    status: {
      defaultValue: undefined,
      exportable: true,
      label: "Status"
    },
    tags: {
      defaultValue: undefined,
      exportable: true,
      label: "Tag"
    },
    tagOperator: {
      defaultValue: undefined,
      exportable: true,
      label: "Filter tags by:"
    },
    type: {
      defaultValue: undefined,
      exportable: true,
      label: "Filter type"
    },
  }

  const EMPTY_ADVANCED_SORTING_ITEM = {
    columnName: {
      defaultValue: undefined,
      exportable: true,
      label: "Column name"
    },
    sortOrder: {
      defaultValue: undefined,
      exportable: true,
      label: "Sort order"
    },
  }

  const EMPTY_COLUMN_CONFIG = {
    asyncCallType: {
      defaultValue: undefined,
      exportable: true,
      label: "Dynamic Function"
    },
    bgColor: {
      defaultValue: undefined, 
      exportable: true,
      label: "Background color"
    },
    columnAsyncFilters: {
      defaultValue: undefined,
      exportable: true,
      label: "Filters"
    },
    columnAsyncFormula: {
      defaultValue: undefined,
      exportable: true,
      label: "Formula"
    },
    columnAsyncGroupName: {
      defaultValue: undefined,
      exportable: true,
      label: "Data group"
    },
    columnAsyncValueColumn: {
      defaultValue: undefined,
      exportable: true,
      label: "Data column"
    },
    columnDropdownFilters: {
      defaultValue: undefined,
      exportable: true,
      label: "Data filters"
    },
    columnDropdownGroupName: {
      defaultValue: undefined,
      exportable: true,
      label: "Data group"
    },
    columnDropdownOptions: {
      defaultValue: undefined,
      exportable: true,
      label: "Dropdown options"
    },
    columnDropdownSymbolKey: {
      defaultValue: undefined,
      exportable: true,
      label: "Data key"
    },
    columnId: {
      defaultValue: undefined,
      exportable: true,
      label: "Column"
    },
    columnType: {
      defaultValue: COLUMN_TYPES.TEXT,
      exportable: true,
      label: "Column type"
    },
    columnWidth: {
      defaultValue: undefined,
      exportable: true,
      label: "Column width"
    },
    connectedColumn: {
      defaultValue: undefined,
      exportable: true,
      label: "Connected Column"
    },
    currentDateFormat: {
      defaultValue: undefined,
      exportable: true,
      label: "Current date format"
    },
    decimalPlaces: {
      defaultValue: undefined,
      exportable: true,
      label: "Decimal places"
    },
    fontSize: {
      defaultValue: undefined, 
      exportable: true,
      label: "Font size",
      mapper: value => value === "11" ? "Small" : value === "12" ? "Medium" : value === "16" ? "Large" : value
    },
    forceUpdateTooltipText: {
      defaultValue: undefined,
      exportable: true,
      label: "Tooltip text"
    },
    key: {
      defaultValue: undefined,
      exportable: true,
      label: "Key"
    },
    isColumnDropdownBasedOnSymbol: {
      defaultValue: false,
      exportable: true,
      label: "Dropdown type"
    },
    isEditable: {
      defaultValue: true,
      exportable: true,
      label: "Disable edit for this column"
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden"
    },
    isCreatedWithFormulaCreator: {
      defaultValue: false,
      exportable: true,
      label: "Is formula"
    },
    label: {
      defaultValue: undefined, 
      exportable: true,
      label: "Label"
    },
    newDateFormat: {
      defaultValue: undefined,
      exportable: true,
      label: "Expected date format"
    },
    numberValidationMinimum: {
      defaultValue: undefined,
      exportable: true,
      label: "Minimum value"
    },
    numberValidationMaximum: {
      defaultValue: undefined,
      exportable: true,
      label: "Maximum value"
    },
    numberValidationIsNumber: {
      defaultValue: false,
      exportable: true,
      label: "Expect number"
    },
    numberValidationIsRequired: {
      defaultValue: false,
      exportable: true,
      label: "Required value"
    },
    requiredValueFormat: {
      defaultValue: undefined,
      exportable: true,
      label: "Required value format"
    },
    roundingPolicy: {
      defaultValue: ROUNDING_POLICIES[0]?.value,
      exportable: true,
      label: "Rounding policy",
      mapper: value => ROUNDING_POLICIES.find(rp => rp.value === value)
    },
    shouldForceUpdateConnectedColumn: {
      defaultValue: false,
      exportable: true,
      label: "Require in-row value update in connected column"
    },
    shouldFormatDate: {
      defaultValue: false,
      exportable: true,
      label: "Format date"
    },
    shouldFormatNumber: {
      defaultValue: false,
      exportable: true,
      label: "Format number"
    },
    shouldFormatPositiveValues: {
      defaultValue: false,
      exportable: true,
      label: "Format positive values"
    },
    shouldValidateNewValues: {
      defaultValue: false,
      exportable: true,
      label: "Validate new values"
    },
    sourceTimezone: {
      defaultValue: TimeService.UTC,
      exportable: true,
      label: "Source timezone"
    },
    symbolValidationIsSymbol: {
      defaultValue: false,
      exportable: true,
      label: "Expect symbol"
    },
    validationValueType: {
      defaultValue: undefined,
      exportable: true,
      label: "Value type"
    },
    targetTimezone: {
      defaultValue: undefined,
      exportable: true,
      label: "Target timezone"
    },
    textAlign: {
      defaultValue: undefined, 
      exportable: true,
      label: "Text alignment"
    },
    textColor: {
      defaultValue: undefined, 
      exportable: true,
      label: "Text color"
    },
    textStyle: {
      defaultValue: undefined, 
      exportable: true,
      label: "Text style"
    },
    unit: {
      defaultValue: undefined,
      exportable: true,
      label: "Data unit:"
    },
    unitOnFront: {
      defaultValue: false,
      exportable: true,
      label: "Display unit:"
    },
  }

  const CURVE_CONFIG = {
    contractSort: {
      defaultValue: CommonEnums.SORT_ORDER.ASCENDING,
      exportable: true,
      label: "Contract sort order",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    csvHeaders: {
      defaultValue: undefined,
      exportable: true,
      label: "Column header",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    fillBlanks: {
      defaultValue: undefined,
      exportable: true,
      label: "Fill blanks",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    labelDateFormat: {
      defaultValue: "yyyy-MM-dd",
      exportable: true,
      label: "Curves date format (select or type)",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    isRelative: {
      defaultValue: "false",
      exportable: true,
      label: "Contract Type",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    periodType: {
      defaultValue: DataTransformEnums.PERIOD_TYPE.MONTH,
      exportable: true,
      label: "Period Type",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    range: {
      defaultValue: DataStorageEnums.DATA_RANGE.LAST,
      exportable: true,
      label: "Date range",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    specificDate: {
      defaultValue: undefined,
      exportable: true,
      label: "Date",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    symbolSize: {
      defaultValue: undefined,
      exportable: true,
      label: "Limit Contracts",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
    withColumnSelector: {
      defaultValue: false,
      exportable: true,
      label: "Allow column selection",
      section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
    },
  }

  const FORMULA_SOURCE_TS_CSV_HEADER = {
    label: "Symbol",
    value: "Symbol",
    name: "Symbol",
    type: DataStorageEnums.CSV_HEADER_TYPE.SYMBOL_KEY
  }

  const FORMULA_SOURCE_CURVE_CSV_HEADER = {
    label: "FuturesRoot",
    value: "FuturesRoot",
    name: "FuturesRoot",
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }

  const getCsvHeaderValue = header => header?.value || header?.name

  const hasRequiredFirstCsvHeader = ({ csvHeaders, requiredHeader }) =>
    getCsvHeaderValue((csvHeaders || [])[0]) === requiredHeader.value

  const isFormulaSourceInputParamsConfig = ({ inputParamsConfig, withLegacyFallback = false }) => {
    if(inputParamsConfig?.basicInputParamsType !== BASIC_INPUT_PARAM_TYPES.TS.value && inputParamsConfig?.basicInputParamsType !== BASIC_INPUT_PARAM_TYPES.CURVE.value) {
      return false
    }

    if(inputParamsConfig?.isFormulaSource === true) {
      return true
    }

    if(!withLegacyFallback) {
      return false
    }

    if(inputParamsConfig.basicInputParamsType === BASIC_INPUT_PARAM_TYPES.TS.value) {
      return inputParamsConfig.csvFormat === DataStorageEnums.DATA_FORMAT.CSV
        && hasRequiredFirstCsvHeader({
          csvHeaders: inputParamsConfig.csvHeaders,
          requiredHeader: FORMULA_SOURCE_TS_CSV_HEADER
        })
    }

    return hasRequiredFirstCsvHeader({
      csvHeaders: inputParamsConfig.csvHeaders,
      requiredHeader: FORMULA_SOURCE_CURVE_CSV_HEADER
    })
  }

  const isFormulaSourceTable = ({ inputParamsConfig, source, withOptionalSave = false }) =>
    source?.type === TILE_SOURCE_TYPES.SOURCE_TILE
      && isFormulaSourceInputParamsConfig({
        inputParamsConfig,
        withLegacyFallback: withOptionalSave
      })

  const INPUT_PARAMS_FIELDS_CONFIG = {
    ...CURVE_CONFIG,
    basicInputParamsType: {
      defaultValue: undefined,
      exportable: true,
      label: "Type",
      section: "Type"
    },
    columns: {
      defaultValue: [],
      exportable: false,
      label: "Columns",
      section: CONFIGURATION_SECTIONS.SOURCE.value
    },
    csvFile: {
      defaultValue: undefined,
      exportable: false,
      label: "Source",
      section: CONFIGURATION_SECTIONS.SOURCE.value
    },
    sql: {
      defaultValue: undefined,
      exportable: true,
      label: "SQL",
      section: CONFIGURATION_SECTIONS.SOURCE.value
    },
    sqlBindings: {
      defaultValue: [],
      exportable: true,
      label: "SQL bindings",
      section: CONFIGURATION_SECTIONS.SOURCE.value
    },
    csvFormat: {
      defaultValue: DataStorageEnums.DATA_FORMAT.NCSV,
      exortable: true,
      label: "CSV format",
      mapper: value => DataStorageReadableConsts.DATA_FORMATS.find(df => df.value === value),
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    csvHeaders: {
      exortable: true,
      label: "CSV headers",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    curveKeys: {
      defaultValue: [],
      exportable: true,
      label: "Keys"
    },
    dateFormat: {
      exportable: true,
      label: "Date format",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    dataRange: {
      defaultValue: DataStorageEnums.DATA_RANGE.BETWEEN,
      exportable: true,
      label: "Data range",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    decimalPrecision: {
      defaultValue: "2",
      exportable: true,
      label: "Decimal precision:",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    description: {
      defaultValue: undefined,
      exportable: true,
      label: "Description",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    fileColumns: {
      defaultValue: undefined,
      exportable: true,
      label: "File columns"
    },
    from: {
      defaultValue: undefined,
      exportable: true,
      label: "From",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    fromSelected: {
      defaultValue: undefined,
      exportable: true,
      label: "From (selected)",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    groupName: {
      defaultValue: undefined,
      exportable: false,
      label: "Group",
      section: CONFIGURATION_SECTIONS.SOURCE.value
    },
    // [{ groupName, source }] saved only for All providers: every environment the group comes from
    groupSources: {
      defaultValue: undefined,
      exportable: false,
      label: "Group environments",
      section: CONFIGURATION_SECTIONS.SOURCE.value
    },
    isHidden: {
      defaultValue: false,
      exportable: true,
      label: "Hidden",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    isHierarchyEnabled: {
      defaultValue: true,
      exportable: true,
      label: "Dynamic value filtering",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    isFormulaSource: {
      defaultValue: false,
      exportable: true,
      label: "Formula source",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    keys: {
      defaultValue: undefined,
      exportable: true,
      label: "Keys"
    },
    lastValue: {
      defaultValue: "500",
      exportable: true,
      label: "Amount",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    lastType: {
      defaultValue: DataStorageEnums.LAST_TYPE.VALUE,
      exportable: true,
      label: "Type",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    metaDataExactMatch: {
      defaultValue: true,
      exportable: true,
      label: "Metadata values exact match",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    patternExactMatch: {
      defaultValue: false,
      exportable: true,
      label: "Symbol keys exact match",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    presets: {
      defaultValue: [],
      exportable: true,
      label: "Presets"
    },
    provider: {
      defaultValue: undefined,
      exportable: false,
      label: "Provider",
      section: CONFIGURATION_SECTIONS.SOURCE.value
    },
    shouldCloseOnSubmit: {
      defaultValue: false,
      exportable: true,
      label: "Close on submit",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    shouldOpenOnStart: {
      defaultValue: false,
      exportable: true,
      label: "Open by default",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    shouldSynchronizeParams: {
      defaultValue: true,
      exportable: true,
      label: "Parameter synchronization",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    submitButtonText: {
      defaultValue: "Show results",
      exportable: true,
      label: "Button text",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    symbolValuesExactMatch: {
      defaultValue: true,
      exportable: true,
      label: "Symbol values exact match",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    timezone: {
      defaultValue: TimeService.UTC,
      exportable: true,
      label: "Timezone",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    title: {
      defaultValue: "Parameters",
      exportable: true,
      label: "Title",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
    to: {
      defaultValue: undefined,
      exportable: true,
      label: "To",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    toSelected: {
      defaultValue: undefined,
      exportable: true,
      label: "To (selected)",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    withDateRange: {
      defaultValue: true,
      exportable: true,
      label: "Date range",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    withMetadata: {
      defaultValue: false,
      exportable: true,
      label: "Include metadata",
      section: CONFIGURATION_SECTIONS.CONFIGURATION.value
    },
    values: {
      defaultValue: undefined,
      exportable: true,
      label: "Values (Table only)"
    },
  }

  const EMPTY_TABLE_ACTION = {
    clickForDetailsEnabled: {
      defaultValue: false,
      exportable: false,
      label: "Enable 'click-to-preview' synchronization"
    },
    columnsRegexp: {
      defaultValue: undefined,
      exportable: false,
      label: "Columns RegExp"
    },
    externalInsightGroupName: {
      defaultValue: undefined,
      exportable: false,
      label: "Insight group"
    },
    externalInsightName: {
      defaultValue: undefined,
      exportable: false,
      label: "Insight name"
    },
    fileExtension: {
      defaultValue: undefined,
      exportable: false,
      label: "File type"
    },
    fileSourceGroup: {
      defaultValue: undefined,
      exportable: false,
      label: "File source group"
    },
    fileType: {
      defaultValue: undefined,
      exportable: false,
      label: "Type"
    },
    formulaNameColumn: {
      defaultValue: undefined,
      exportable: true,
      label: "Column with formula name"
    },
    outputTile: {
      defaultValue: undefined,
      exportable: false,
      label: "Output tile"
    },
    matchFileNameWith: {
      defaultValue: undefined,
      exportable: false,
      label: "Match file name with"
    },
    scrollOnPreview: {
      defaultValue: false,
      exportable: false,
      label: "Scroll on preview"
    },
    scrollOnPreviewTile: {
      defaultValue: undefined,
      exportable: false,
      label: "Choose Tile to scroll to"
    },
    selectedColumns: {
      defaultValue: undefined,
      exportable: false,
      label: "Selected columns"
    },
    showOptionIn: {
      defaultValue: undefined,
      exportable: false,
      label: "Show option in"
    },
    shouldUseExternalInsight: {
      defaultValue: undefined,
      exportable: true,
      label: "Use external insight"
    },
    targetColumn: {
      defaultValue: undefined,
      exportable: false,
      label: "Column to show/hide"
    },
    targetColumnRegexp: {
      defaultValue: undefined,
      exportable: false,
      label: "Target column RegExp"
    },
    tileIds: {
      defaultValue: undefined,
      exportable: false,
      label: "Choose Input params to synchronize"
    },
  }

  const EMPTY_CURVE_SET = {
    column: {
      defaultValue: undefined,
      exportable: false,
      label: "Column"
    },
    groupName: {
      defaultValue: undefined,
      exportable: false,
      label: "Group"
    },
    product: {
      defaultValue: undefined,
      exportable: false,
      label: "Product"
    },
    root: {
      defaultValue: undefined,
      exportable: false,
      label: "Root"
    },
  }

  const EMPTY_PASSED_FILE = {
    label: {
      defaultValue: "File",
      exportable: false,
      label: "Label"
    },
    value: {
      defaultValue: undefined,
      exportable: false,
      label: "Source data"
    },
  }

  const EMPTY_FUNCTIONS_FILE = {
    value: {
      defaultValue: undefined,
      exportable: false,
      label: "Custom file"
    },
  }

  const EMPTY_FORMULA_PANEL_METADATA_KEY = {
    allowCreateNewValues: {
      defaultValue: false,
      exportable: false,
      label: "Allow to create new values"
    },
    defaultValue: {
      defaultValue: undefined,
      exportable: false,
      label: "Default value"
    },
    groupName: {
      defaultValue: undefined,
      exportable: false,
      label: "Group"
    },
    isHidden: {
      defaultValue: false,
      exportable: false,
      label: "Hidden"
    },
    label: {
      defaultValue: undefined,
      exportable: false,
      label: "Label"
    },
    symbolKey: {
      defaultValue: undefined,
      exportable: false,
      label: "Key"
    },
    useFormulaNameAsDefault: {
      defaultValue: false,
      exportable: false,
      label: "Use Formula name as default"
    },
    valueType: {
      defaultValue: "DROPDOWN",
      exportable: false,
      label: "Value type"
    },
  }

  const FORMULA_PANEL_METADATA_VALUE_TYPE = {
    DROPDOWN: "DROPDOWN",
    TEXT: "TEXT"
  }
  const FORMULA_PANEL_METADATA_VALUE_TYPES = [{
    label: "Dropdown",
    value: FORMULA_PANEL_METADATA_VALUE_TYPE.DROPDOWN
  }, {
    label: "Text",
    value: FORMULA_PANEL_METADATA_VALUE_TYPE.TEXT
  }]
  const FORMULA_PANEL_USE_FORMULA_NAME_OPTION = {
    label: "Use formula name",
    value: "__USE_FORMULA_NAME__"
  }
  const FORMULA_CONSTANTS_SOURCES = {
    DEFAULT: "default",
    FILE: "file",
    USER: "user"
  }
  const METADATA_STANDARD_GROUP = { label: "NG Metadata Standards", value: "NG Metadata Standards" }
  const NGPCD = "NGPCD"

  const FORMULA_RECALCULATE_PERIODS = {
    EVERY_DAY: {
      label: "Every day",
      value: "EVERY_DAY"
    },
    EVERY_BUSINESS_DAY: {
      label: "Every business day",
      value: "EVERY_BUSINESS_DAY"
    }
  }

  const FORMULA_RECALCULATE_MODES = {
    CONTINUOUS: {
      label: "Continuously",
      value: FormulaEnums.FORMULA_SYMBOL_ARRIVAL_MODE.CONTINUOUS
    },
    WINDOWED: {
      label: "Within period",
      value: FormulaEnums.FORMULA_SYMBOL_ARRIVAL_MODE.WINDOWED
    }
  }

  const DEFAULT_SAW_PIPELINE = {
    label: "Default pipeline",
    value: undefined
  }

  const DEFAULT_FORMULA_PANEL = {
    functions: {
      defaultValue: [],
      exportable: false,
      label: "Functions"
    },
    metadataKeys: {
      defaultValue: [],
      exportable: false,
      label: "Metadata keys"
    },
    defaultGroup: {
      defaultValue: undefined,
      exportable: false,
      label: "Default group"
    },
    withOptionalSave: {
      defaultValue: false,
      exportable: false,
      label: "Enable creation by viewer"
    },
    shouldRecalculateOnSymbolUpdate: {
      defaultValue: false,
      exportable: false,
      label: "Run formula automatically on data updates",
    },
    recalculateHolidayCalendar: {
      defaultValue: undefined,
      exportable: false,
      label: "Holiday calendar"
    },
    recalculateMode: {
      defaultValue: FORMULA_RECALCULATE_MODES.WINDOWED.value,
      exportable: false,
      label: "Check for updates"
    },
    recalculatePeriod: {
      defaultValue: FORMULA_RECALCULATE_PERIODS.EVERY_DAY.value,
      exportable: false,
      mapper: value => Object.values(FORMULA_RECALCULATE_PERIODS).find(s => s.value === value),
      label: "Period"
    },
    recalculatePipeline: {
      defaultValue: undefined,
      exportable: false,
      label: "On update, run pipeline (empty for default)",
      mapper: val => val === undefined ? DEFAULT_SAW_PIPELINE : val
    },
    recalculateTimeFrom: {
      defaultValue: "00:00:00",
      exportable: false,
      label: "From"
    },
    recalculateTimeTo: {
      defaultValue: "23:59:59",
      exportable: false,
      label: "To"
    },
    recalculateTimezone: {
      defaultValue: "UTC",
      exportable: false,
      label: "Timezone"
    },
    shouldShowAdvancedOptions: {
      defaultValue: false,
      exportable: false,
      label: "Show advanced options"
    }
  }
  const FORMULA_PANEL_DEFAULT_VALUE = Object.fromEntries(Object.entries(DEFAULT_FORMULA_PANEL).map(([key, value]) => [key, value.defaultValue]))

  const EMPTY_DIAGRAM_NODE = {
    attributes: {
      defaultValue: [],
      exportable: false,
      label: "Attributes"
    },
    connections: {
      defaultValue: [],
      exportable: false,
      label: "Connections"
    },
    label: {
      defaultValue: undefined,
      exportable: false,
      label: "Label" 
    },
    nodeWidth: {
      defaultValue: "160",
      exportable: false,
      label: "Node width (px)",
      section: CONFIGURATION_SECTIONS.STYLE.value
    },
    pipeline: {
      defaultValue: undefined,
      exportable: false,
      label: "Source pipeline" 
    },
    type: {
      defaultValue: DIAGRAM_NODES.PIPELINE.type,
      exportable: false,
      label: "Type" 
    },
    withImportStatuses: {
      defaultValue: false,
      exportable: false,
      label: "Import statuses",
      section: "Import Pipeline Attributes"
    },
    withImportTriggers: {
      defaultValue: false,
      exportable: false,
      label: "Import triggers",
      section: "Import Pipeline Attributes"
    },
    withOpenButton: {
      defaultValue: false,
      exportable: false,
      label: "Open pipeline button",
      section: "Import Pipeline Attributes"
    },
  }

  const EMPTY_DIAGRAM_CONNECTION = {
    connectionType: {
      defaultValue: undefined,
      exportable: false,
      label: "Type" 
    },
    id: {
      defaultValue: undefined,
      exportable: false,
      label: "ID" 
    },
    label: {
      defaultValue: undefined,
      exportable: false,
      label: "Label" 
    },
    tile: {
      defaultValue: undefined,
      exportable: false,
      label: "Choose tile" 
    },
  }

  const EMPTY_DIAGRAM_ATTRUBUTE = {
    action: {
      defaultValue: AVAILABLE_ACTIONS.PREVIEW,
      exportable: false,
      label: "Action" 
    },
    file: {
      defaultValue: undefined,
      exportable: false,
      label: "File" 
    },
    label: {
      defaultValue: "Attribute",
      exportable: false,
      label: "Label" 
    },
  }

  const EMPTY_ROW_CONFIG_TABLE = {
    bgColor: {
      defaultValue: undefined, 
      exportable: true,
      label: "Background color"
    },
    fontSize: {
      defaultValue: undefined, 
      exportable: true,
      label: "Font size",
      mapper: value => value === "11" ? "Small" : value === "12" ? "Medium" : value === "16" ? "Large" : value
    },
    label: {
      defaultValue: undefined, 
      exportable: true,
      label: "Label"
    },
    rowId: {
      defaultValue: undefined, 
      exportable: true,
      label: "Row ID"
    },
    textAlign: {
      defaultValue: undefined, 
      exportable: true,
      label: "Text alignment"
    },
    textColor: {
      defaultValue: undefined, 
      exportable: true,
      label: "Text color"
    },
    textStyle: {
      defaultValue: undefined, 
      exportable: true,
      label: "Text style"
    },
  }

  const TILE_FIELDS_CONFIG = {
    [TILE_TYPES.CHART]: {
      ...ACTIONS,
      ...CURVE_CONFIG,
      allowDataSelection: {
        defaultValue: true,
        exportable: true,
        label: "Data selection",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      axisTickFontSize: {
        defaultValue: "12",
        exportable: true,
        label: "Axis tick size",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      basicChartType: {
        defaultValue: undefined,
        exportable: false,
        label: "Chart type",
        section: "Chart type"
      },
      chartLayout: {
        defaultValue: CHART_LAYOUT.HORIZONTAL.value,
        label: "Chart layout",
        exportable: true,
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      chartMarginBottom: {
        defaultValue: undefined,
        exportable: true,
        label: "Bottom",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      chartMarginLeft: {
        defaultValue: undefined,
        exportable: true,
        label: "Left",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      chartMarginRight: {
        defaultValue: undefined,
        exportable: true,
        label: "Right",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      chartMarginTop: {
        defaultValue: undefined,
        exportable: true,
        label: "Top",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      colorPalette: {
        defaultValue: DEFAULT_CHART_COLOR_PALETTE,
        exportable: true,
        label: "Color palette",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      dots: {
        defaultValue: false,
        exportable: true,
        label: "Dots on data points",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      withAreaDiff: {
        defaultValue: false,
        exportable: true,
        label: "Show area difference",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      conditionalFormatting: {
        defaultValue: [],
        exportable: true,
        label: "Conditional formatting"
      },
      curveKeys: {
        defaultValue: [],
        exportable: true,
        label: "Keys"
      },
      showInTooltip: {
        defaultValue: false,
        exportable: true,
        label: "Show in tooltip",
      },
      showValueOnDataPoints: {
        defaultValue: false,
        exportable: true,
        label: "Show value on data points",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      dataPointLabelValueType: {
        defaultValue: DATA_POINT_LABEL_VALUE_TYPES.NORMALIZED,
        exportable: true,
        label: "Display as",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tooltipConfig: {
        defaultValue: [],
        exportable: true,
        label: "Chart tooltip"
      },
      dateFormat: {
        defaultValue: undefined,
        exportable: true,
        label: "Date format",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      downloadable: {
        defaultValue: true,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      filterZero: {
        defaultValue: false,
        exportable: true,
        label: "Filter zero",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      referenceData: {
        defaultValue: [],
        exportable: true,
        label: "Reference data"
      },
      normalizationLastType: {
        defaultValue: DataStorageEnums.LAST_TYPE.VALUE,
        exportable: true,
        label: "Type",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      normalizationLastTypeAmount: {
        defaultValue: "1",
        exportable: true,
        label: "Amount",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      from: {
        defaultValue: undefined,
        exportable: true,
        label: "From",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      fromSelected: {
        defaultValue: undefined,
        exportable: true,
        label: "From (selected)",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      interpolation: {
        defaultValue: CHART_INTERPOLATIONS.MONOTONE.value,
        exportable: true,
        label: "Interpolation type",
        mapper: value => Object.values(CHART_INTERPOLATIONS).find(s => s.value === value),
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      keyAsTime: {
        defaultValue: false,
        exportable: false,
        label: "Symbol keys as time",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      keyAsTimeSymbol: {
        defaultValue: undefined,
        exportable: true,
        label: "Symbol key",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      keyAsTimeTimezone: {
        defaultValue: "UTC",
        exportable: true,
        label: "Symbol key timezone",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      keyIsNumber: {
        defaultValue: false,
        exportable: true,
        label: "Symbol key is a number",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      keySelection: {
        defaultValue: [],
        exportable: false,
        label: "User data selection"
      },
      labelBasedOn: {
        defaultValue: LABEL_BASED_ON.DEFAULT,
        exportable: true,
        label: "Display labels names based on",
        section: CONFIGURATION_SECTIONS.DATA_LABELS.value,
      },
      labelJoinColumn: {
        defaultValue: false,
        exportable: true,
        label: "Add column",
        section: CONFIGURATION_SECTIONS.DATA_LABELS.value,
      },
      labelJoinSign: {
        defaultValue: "-",
        exportable: true,
        label: "Separator",
        section: CONFIGURATION_SECTIONS.DATA_LABELS.value,
      },
      labelKey: {
        defaultValue: undefined,
        exportable: false,
        label: "Choose key",
        section: CONFIGURATION_SECTIONS.DATA_LABELS.value,
      },
      lastType: {
        defaultValue: DataStorageEnums.LAST_TYPE.VALUE,
        exportable: true,
        label: "Type",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      lastTypeAmount: {
        defaultValue: "1",
        exportable: true,
        label: "Amount",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      leftAxis: {
        defaultValue: CHART_AXIS_CONFIG,
        exportable: true,
        isSubConfiguration: true,
        label: "Left Y Axis"
      },
      legendSortOrder: {
        defaultValue: undefined,
        exportable: true,
        label: "Legend sort order",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      metadataTooltip: {
        defaultValue: false,
        exportable: true,
        label: "Show chart metadata tooltip",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      output: {
        defaultValue: Object.fromEntries(Object.entries(CHART_OUTPUT_CONFIG_DEFAULT).map(([k, v]) => [k, v.defaultValue])),
        exportable: true,
        label: "Output configuration:"
      },
      ...PARTNERS,
      range: {
        defaultValue: DataStorageEnums.DATA_RANGE.LAST,
        exportable: true,
        label: "Date range",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      reverseXAxis: {
        defaultValue: false,
        exportable: true,
        label: "Reverse ticks",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      rightAxis: {
        defaultValue: CHART_AXIS_CONFIG,
        exportable: true,
        isSubConfiguration: true,
        label: "Right Y Axis"
      },
      rowsLimit: {
        defaultValue: undefined,
        exportable: true,
        label: "Limit number of rows",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      samplerBucketSize: {
        defaultValue: undefined,
        exportable: true,
        label: "Sampler bucket size",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonFirstMonth: {
        defaultValue: "1",
        exportable: true,
        label: "Choose month",
        mapper: value => SEASON_MONTHS_OPTIONS.find(m => m.value === value),
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonMonth: {
        defaultValue: SEASON_MONTH.FIRST.value,
        exportable: true,
        label: "Season month",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonalityType: {
        defaultValue: SEASONALITY_TYPE.CALENDAR.value,
        exportable: true,
        label: "Compare base",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonalityIsForecastSplitDisabled: {
        defaultValue: false,
        exportable: true,
        label: "Combine current year data with forecast",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      defaultYears: {
        defaultValue: [],
        exportable: true,
        label: "Default years",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonalityDefaultExpiryDate: {
        defaultValue: DEFAULT_SEASONALITY_EXPIRY_DATE,
        exportable: true,
        label: "Default expiry date",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonalityIsSpreadEnabled: {
        defaultValue: false,
        exportable: true,
        label: "Use spread",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonalityDefaultZoomValue: {
        defaultValue: undefined,
        exportable: true,
        label: "Days to show before expiry",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      seasonalityArePickersHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.PICKERS.value
      },
      seasonalityPickersDataLabelsType: {
        defaultValue: SEASONALITY_PICKERS_DATA_LABEL_TYPES.DEFAULT.value,
        exportable: true,
        label: "Display labels names based on",
        mapper: value => Object.values(SEASONALITY_PICKERS_DATA_LABEL_TYPES).find(type => type.value === value),
        section: CONFIGURATION_SECTIONS.PICKERS.value
      },
      seasonalityPickersSpecificKey: {
        defaultValue: undefined,
        exportable: true,
        label: "Choose key",
        section: CONFIGURATION_SECTIONS.PICKERS.value
      },
      seasonalityPickersContractResolverLabels: {
        defaultValue: undefined,
        exportable: true,
        label: "Choose labels",
        section: CONFIGURATION_SECTIONS.PICKERS.value
      },
      seasonalityPickersContractResolverSeparator: {
        defaultValue: " / ",
        exportable: true,
        label: "Separator",
        section: CONFIGURATION_SECTIONS.PICKERS.value
      },
      shortDatapreps: {
        defaultValue: undefined,
        exportable: false,
        label: "Short data preps"
      },
      showSourceFileVersions: {
        defaultValue: false,
        exportable: true,
        label: "Source file versions",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      showInputParamsOnTile: {
        defaultValue: true,
        exportable: true,
        label: "Parameters shown on tile",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      source: {
        defaultValue: undefined,
        exportable: false,
        label: "Source",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      sourceDateFormat: {
        defaultValue: "yyyy-MM-dd",
        exportable: true,
        label: "Source date format (select or type)",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      sourceFileColumns: {
        defaultValue: [],
        exportable: false,
        label: "Source file columns"
      },
      sourceSymbols: {
        defaultValue: {},
        exportable: false,
        label: "Source symbols"
      },
      templateContent: {
        defaultValue: undefined,
        exportable: false,
        label: "Template content"
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      timeZone: {
        defaultValue: TimeService.UTC,
        exportable: true,
        label: "Time zone",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      title: {
        defaultValue: "Line chart",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      to: {
        defaultValue: undefined,
        exportable: true,
        label: "To",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      toSelected: {
        defaultValue: undefined,
        exportable: true,
        label: "To (selected)",
        section: CONFIGURATION_SECTIONS.CURVE_CONFIGURATION.value
      },
      type: {
        defaultValue: TILE_TYPES.CHART,
        exportable: true,
        label: "Type"
      },
      tooltip: {
        defaultValue: true,
        exportable: true,
        label: "Hover tooltip",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      useSampling: {
        defaultValue: false,
        exportable: true,
        label: "Use sampling",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      withDateRange: {
        defaultValue: false,
        exportable: true,
        label: "Date range",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      withImageTitle: {
        defaultValue: false,
        exportable: true,
        label: "Image with title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      withMetadata: {
        defaultValue: false,
        exportable: false,
        label: "Include metadata",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      xAxisCustomDomain: {
        defaultValue: undefined,
        exportable: true,
        label: "Custom ticks domain",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisFormatDomainOperation: {
        defaultValue: false,
        exportable: true,
        label: "Format domain operation:",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisFormatDomainValue: {
        defaultValue: false,
        exportable: true,
        label: "Format domain by:",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisFormatDomainUnit: {
        defaultValue: false,
        exportable: true,
        label: "Format domain unit:",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisLabel: {
        defaultValue: undefined,
        exportable: true,
        label: "Label",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisOrientation: {
        defaultValue: "bottom",
        exportable: true,
        label: "Line position",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisPaddingTop: {
        defaultValue: 16,
        exportable: true,
        label: "Top",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisPaddingRight: {
        defaultValue: 30,
        exportable: true,
        label: "Right",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisPaddingBottom: {
        defaultValue: undefined,
        exportable: true,
        label: "Bottom",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisPaddingLeft: {
        defaultValue: 30,
        exportable: true,
        label: "Left",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisTickAngle: {
        defaultValue: "0",
        exportable: true,
        label: "Tick angle",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisTickDaysInterval: {
        defaultValue: undefined,
        exportable: true,
        label: "Tick days interval",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisType: {
        defaultValue: AXIS_TYPES.CATEGORY?.value,
        exportable: true,
        label: "Axis data type",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisUnit: {
        defaultValue: undefined,
        exportable: true,
        label: "Select or type unit:",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xAxisUnitOnFront: {
        defaultValue: false,
        exportable: true,
        label: "Display unit",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xColumn: {
        defaultValue: undefined,
        exportable: false,
        label: "Source column",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      xDomain: {
        defaultValue: undefined,
        exportable: true,
        label: "Domain:",
        section: CONFIGURATION_SECTIONS.X_AXIS.value
      },
      yAxisPaddingBottom: {
        defaultValue: 16,
        exportable: false,
        label: "Bottom",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      yAxisPaddingLeft: {
        defaultValue: undefined,
        exportable: false,
        label: "Left",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      yAxisPaddingRight: {
        defaultValue: undefined,
        exportable: false,
        label: "Right",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      yAxisPaddingTop: {
        defaultValue: 16,
        exportable: false,
        label: "Top",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      yColumn: {
        defaultValue: undefined,
        exportable: false,
        label: "Default column",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      yGroupName: {
        defaultValue: undefined,
        exportable: false,
        label: "Group",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      yMovingAverage: {
        defaultValue: undefined,
        exportable: true,
        label: "Moving average",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      yMovingAverageValue: {
        defaultValue: undefined,
        exportable: true,
        label: "Moving average value",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      ySymbolList: {
        defaultValue: [],
        exportable: false,
        label: "Symbol list",
        section: CONFIGURATION_SECTIONS.Y_AXIS.value
      },
      withUpdateInfo: {
        defaultValue: false,
        exportable: true,
        label: "Display update information",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
    },
    [TILE_TYPES.TABLE]: {
      ...ACTIONS,
      ...CURVE_CONFIG,
      ...EMPTY_FORMULA_PANEL_METADATA_KEY,
      allowColumnSelection: {
        defaultValue: false,
        exportable: true,
        label: "Allow column selection",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      columnSelectionBlackList: {
        defaultValue: [],
        exportable: true,
        label: "Blacklisted columns",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      contextMenuActions: {
        defaultValue: [],
        exportable: true ,
        label: "Right-click options"
      },
      customActionsEnabled: {
        defaultValue: false,
        exportable: true,
        label: "Enable right-click actions",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      columns: {
        defaultValue: [],
        exportable: true,
        label: "Columns"
      },
      conditionalFormatting: {
        defaultValue: [],
        exportable: true,
        label: "Conditional formatting"
      },
      displayColumnsOrder: {
        defaultValue: "",
        exportable: true,
        label: "Display columns order",
        section: CONFIGURATION_SECTIONS.CUSTOMIZATION.value
      },
      downloadable: {
        defaultValue: true,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      editable: {
        defaultValue: false,
        exportable: true,
        label: "Editable by viewer",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      horizontalAlign: {
        defaultValue: HORIZONTAL_ALIGNS_OBJECT.LEFT,
        exportable: true,
        label: "Position",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      isThereAnyTableData: {
        defaultValue: false,
        exportable: false,
        label: "Is there any table data"
      },
      ...PARTNERS,
      rowConfigs: {
        defaultValue: [getEmptyRowConfig(ROW_CONFIGS.HEADER), getEmptyRowConfig(ROW_CONFIGS.EVEN), getEmptyRowConfig(ROW_CONFIGS.ODD), getEmptyRowConfig(ROW_CONFIGS.FOOTER)],
        exportable: true,
        label: CONFIGURATION_SECTIONS.CUSTOMIZATION.label
      },
      rowShortcut: {
        defaultValue: undefined,
        exportable: true,
        label: "Row shortcut",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
      defaultSortBy: {
        defaultValue: undefined,
        exportable: true,
        label: "Default sort by"
      },
      defaultSortOrder: {
        defaultValue: undefined,
        exportable: true,
        label: "Default sort order"
      },
      showSourceFileVersions: {
        defaultValue: false,
        exportable: true,
        label: "Source file versions",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      showInputParamsOnTile: {
        defaultValue: true,
        exportable: true,
        label: "Parameters shown on tile",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      sortable: {
        defaultValue: false,
        exportable: true,
        label: "Sortable",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      source: {
        defaultValue: undefined,
        exportable: false,
        label: "Source",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      isAdvancedSorting: {
        defaultValue: false,
        exportable: true,
        label: "Advanced sorting",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      advancedSortingConfig: {
        defaultValue: [],
        exportable: true,
        label: "Advanced sorting"
      },
      formulaPanel: {
        defaultValue: FORMULA_PANEL_DEFAULT_VALUE,
        exportable: true,
        label: "Formula panel"
      },
      isForceUpdateValidationError: {
        defaultValue: false,
        exportable: false,
        label: "Validation error",
      },
      isValidationError: {
        defaultValue: false,
        exportable: false,
        label: "Validation error"
      },
      stickyBottomRows: {
        defaultValue: undefined,
        exportable: true,
        label: "Sticky bottom rows",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      stickyLeftColumns: {
        defaultValue: undefined,
        exportable: true,
        label: "Sticky left columns",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      stickyRightColumns: {
        defaultValue: undefined,
        exportable: true,
        label: "Sticky right columns",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      stickyTopRows: {
        defaultValue: undefined,
        exportable: true,
        label: "Sticky top rows",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      templateContent: {
        defaultValue: undefined,
        exportable: undefined,
        label: "Template content"
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      title: {
        defaultValue: "Table",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.TABLE,
        exportable: true,
        label: "Type"
      },
      withControls: {
        defaultValue: false,
        exportable: true,
        label: "With controls",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      withUpdateInfo: {
        defaultValue: false,
        exportable: true,
        label: "Display update information",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      withCorrections: {
        defaultValue: false,
        exportable: true,
        label: "Enable corrections",
        section: CONFIGURATION_SECTIONS.ADVANCED.value
      },
    },
    [TILE_TYPES.TEXT]: {
      ...ACTIONS,
      downloadable: {
        defaultValue: true,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      editable: {
        defaultValue: false,
        exportable: true,
        label: "Editable by viewer",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      horizontalAlign: {
        defaultValue: HORIZONTAL_ALIGNS_OBJECT.LEFT,
        exportable: true,
        label: "Position",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      showSourceFileVersions: {
        defaultValue: false,
        exportable: true,
        label: "Source file versions",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      source: {
        defaultValue: undefined,
        exportable: false,
        label: "Source Markdown file",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      ...PARTNERS,
      templateContent: {
        defaultValue: undefined,
        exportable: false,
        label: "Template content"
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      title: {
        defaultValue: "Text section",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.TEXT,
        exportable: true,
        label: "Type"
      },
    },
    [TILE_TYPES.CODE]: {
      ...ACTIONS,
      codeType: {
        defaultValue: undefined,
        exportable: true,
        label: "Code type",
        mapper: value => Object.values(CODE_TYPES).find(ct => ct.value === value),
        section: "Code type",
      },
      downloadable: {
        defaultValue: false,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      editable: {
        defaultValue: false,
        exportable: true,
        label: "Editable by viewer",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      isPdfGenerationEnabled: {
        defaultValue: false,
        exportable: true,
        label: "Enable PDF generation",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      isFtlActive: {
        defaultValue: true,
        exportable: true
      },
      outputLayout: {
        defaultValue: undefined,
        exportable: false,
        label: "Files layout",
        mapper: value => DataPrepReadableConsts.OUTPUT_LAYOUTS.find(d => d.value === value),
        section: CONFIGURATION_SECTIONS.PASSED_FILES.value
      },
      ...PARTNERS,
      passedFiles: {
        defaultValue: [],
        exportable: false,
        label: CONFIGURATION_SECTIONS.PASSED_FILES.label
      },
      scale: {
        defaultValue: 100,
        exportable: true,
        label: "Zoom",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      showSourceFileVersions: {
        defaultValue: false,
        exportable: true,
        label: "Source file versions",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      source: {
        defaultValue: undefined,
        exportable: false,
        label: "Source file",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      templateContent: {
        defaultValue: undefined,
        exportable: false,
        label: "Template content"
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      title: {
        defaultValue: "Code section",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.CODE,
        exportable: true,
        label: "Type"
      },
    },
    [TILE_TYPES.PDF]: {
      ...ACTIONS,
      downloadable: {
        defaultValue: true,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...PARTNERS,
      source: {
        defaultValue: undefined,
        exportable: false,
        label: "Source PDF",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      scale: {
        defaultValue: 100,
        exportable: true,
        label: "Zoom"
      },
      showSourceFileVersions: {
        defaultValue: false,
        exportable: true,
        label: "Source file versions",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      templateContent: {
        defaultValue: undefined,
        exportable: false,
        label: "Template content"
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      title: {
        defaultValue: "New PDF",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.PDF,
        exportable: true,
        label: "Type"
      },
    },
    [TILE_TYPES.IMAGE]: {
      ...ACTIONS,
      downloadable: {
        defaultValue: true,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      horizontalAlign: {
        defaultValue: HORIZONTAL_ALIGNS_OBJECT.LEFT,
        exportable: true,
        label: "Image position",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      source: {
        defaultValue: undefined,
        exportable: false,
        label: "Source image",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      showSourceFileVersions: {
        defaultValue: false,
        exportable: true,
        label: "Source file versions",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...PARTNERS,
      templateContent: {
        defaultValue: undefined,
        exportable: false,
        label: "Template content"
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      title: {
        defaultValue: "New image",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.IMAGE,
        exportable: true,
        label: "Type"
      },
    },
    [TILE_TYPES.AUTO_PIPELINE]: {
      approximateTime: {
        defaultValue: undefined,
        exportable: true,
        label: "Fixed run time estimation (in minutes)",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      description: {
        defaultValue: undefined,
        exportable: true,
        label: "Description",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      params: {
        defaultValue: [],
        exportable: false,
        label: "Parameters"
      },
      pipeline: {
        defaultValue: undefined,
        exportable: false,
        label: "Source pipeline",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      resultsAction: {
        defaultValue: AUTO_PIPELINE_RESULTS_ACTION.OPEN_SOURCE_PIPELINE,
        exportable: true,
        label: "Results action",
        mapper: value => AUTO_PIPELINE_RESULTS_ACTIONS.find(r => r.value === value),
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      resultsActionInsight: {
        defaultValue: undefined,
        exportable: true,
        label: "Results insight",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      shouldUseOriginalPipeline: {
        defaultValue: false,
        exportable: true,
        label: "Use original pipeline",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      submitButtonText: {
        defaultValue: "Submit",
        exportable: true,
        label: "Button text",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tilesToRefresh: {
        defaultValue: undefined,
        exportable: false,
        label: "Tile to refresh after pipeline execution",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      title: {
        defaultValue: "Auto pipeline",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.AUTO_PIPELINE,
        exportable: false,
        label: "Type"
      },
      withGame: {
        defaultValue: true,
        exportable: true,
        label: "Enable game on loading time",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      }
    },
    [TILE_TYPES.STATUS]: {
      ...ACTIONS,
      autoRefreshInterval: {
        defaultValue: "10",
        exportable: true,
        label: "Every (in seconds)",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      limit: {
        defaultValue: "10",
        exportable: true,
        label: "Number of statuses",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      pipelineItems: {
        defaultValue: [],
        exportable: false,
        label: "Pipelines"
      },
      statusFilters: {
        defaultValue: [],
        exportable: false,
        label: "Filters"
      },
      statusesSelectedBy: {
        defaultValue: STATUS_SELECTED_BY.FILTER,
        exportable: false,
        label: "Statuses shown by",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      statusType: {
        defaultValue: undefined,
        exportable: false,
        label: "Status source",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.HALF,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.HALF,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      title: {
        defaultValue: "Statuses",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      type: {
        defaultValue: TILE_TYPES.STATUS,
        exportable: false,
        label: "Type"
      },
      withAutoRefresh: {
        defaultValue: false,
        exportable: true,
        label: "Enable auto refresh",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      withStatusesLink: {
        defaultValue: false,
        exportable: true,
        label: "Add link to the item",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
    },
    [TILE_TYPES.DIAGRAM]: {
      diagram: {
        defaultValue: undefined,
        exportable: false,
        label: "Diagram"
      },
      downloadable: {
        defaultValue: true,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      links: {
        defaultValue: [],
        exportable: false,
        label: "Links"
      },
      nodes: {
        defaultValue: {},
        exportable: false,
        label: "Nodes"
      },
      positions: {
        defaultValue: {},
        exportable: false,
        label: "Positions"
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      title: {
        defaultValue: "New diagram",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.DIAGRAM,
        exportable: false,
        label: "Type"
      },
    },
    [TILE_TYPES.SEARCH]: {
      basicSearchType: {
        defaultValue: undefined,
        exportable: true,
        label: "Type"
      },
      contractSort: {
        defaultValue: CommonEnums.SORT_ORDER.ASCENDING,
        exportable: true,
        label: "Contract sort order",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      columns: {
        defaultValue: [],
        exportable: true,
        label: "Columns",
        mapper: value => value.map(bc => bc === "*" ? ({ label: "All", value: "*" }) : ({ label: bc, value: bc })),
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      column: {
        defaultValue: undefined,
        exportable: true,
        label: "Column",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      columnWidth: {
        defaultValue: "88",
        exportable: true,
        label: "Column width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      csvFormat: {
        defaultValue: DataStorageEnums.DATA_FORMAT.NCSV,
        exortable: true,
        label: "CSV format",
        mapper: value => DataStorageReadableConsts.DATA_FORMATS.find(df => df.value === value),
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      csvHeaders: {
        exportable: true,
        label: "CSV headers",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      csvFile: {
        defaultValue: undefined,
        exportable: true,
        label: "Source",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      dateFormat: {
        defaultValue: undefined,
        exportable: true,
        label: "Date format",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      isRelative: {
        defaultValue: "false",
        exportable: true,
        label: "Contract Type",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      filters: {
        defaultValue: [],
        exportable: true,
        label: "Default Filters"
      },
      fillBlanks: {
        defaultValue: undefined,
        exportable: true,
        label: "Fill blanks",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      from: {
        defaultValue: undefined,
        exportable: true,
        label: "From",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      fromSelected: {
        defaultValue: DataStorageEnums.DATE_BETWEEN_TYPE.DATE,
        exportable: true,
        label: "From (selected)",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      fromTime: {
        defaultValue: undefined,
        exportable: true,
        label: "From (time)",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      groupName: {
        defaultValue: undefined,
        exportable: true,
        label: "Group",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      isFormulaSource: {
        defaultValue: false,
        exportable: true,
        label: "Formula source",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      groupNames: {
        defaultValue: undefined,
        exportable: true,
        label: "Groups",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      // [{ groupName, source }] saved only for All providers, so every group can be asked on its own environment
      groupSources: {
        defaultValue: undefined,
        exportable: true,
        label: "Groups environments",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      lastType: {
        defaultValue: DataStorageEnums.LAST_TYPE.VALUE,
        exportable: true,
        label: "Type",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      lastTypeAmount: {
        defaultValue: "50",
        exportable: true,
        label: "Amount",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      periodType: {
        defaultValue: DataTransformEnums.PERIOD_TYPE.MONTH,
        exportable: true,
        label: "Period Type",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      range: {
        defaultValue: DataStorageEnums.DATA_RANGE.LAST,
        exportable: true,
        label: "Date range",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      symbolSize: {
        defaultValue: undefined,
        exportable: true,
        label: "Limit Contracts",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      provider: {
        defaultValue: undefined,
        exportable: true,
        label: "Provider",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      timeZone: {
        defaultValue: undefined,
        exportable: true,
        label: "Time zone",
        mapper: value => [{ label: "Source", value: undefined, tooltip: "Use source data time zone" }].concat(TimeService.TIME_ZONES.map(t => ReduxService.updateObject(t, { tooltipPosition: "top" }))).find(t => t.value === value),
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      title: {
        defaultValue: "Data exploration",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      to: {
        defaultValue: undefined,
        exportable: true,
        label: "To",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      toSelected: {
        defaultValue: DataStorageEnums.DATE_BETWEEN_TYPE.DATE,
        exportable: true,
        label: "To (selected)",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      toTime: {
        defaultValue: undefined,
        exportable: true,
        label: "To (time)",
        section: CONFIGURATION_SECTIONS.CONFIGURATION.value
      },
      type: {
        defaultValue: TILE_TYPES.SEARCH,
        exportable: true,
        label: "Type"
      },
      withAiSearch: {
        defaultValue: false,
        exportable: true,
        label: "With AI Search",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      withRequestData: {
        defaultValue: false,
        exportable: true,
        label: "With Request Data",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
    },
    [TILE_TYPES.STATS]: {
      ...ACTIONS,
      columns: {
        defaultValue: [],
        exportable: true,
        label: "Columns"
      },
      conditionalFormatting: {
        defaultValue: [],
        exportable: true,
        label: "Conditional formatting"
      },
      downloadable: {
        defaultValue: true,
        exportable: true,
        label: "Download",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      horizontalAlign: {
        defaultValue: HORIZONTAL_ALIGNS_OBJECT.LEFT,
        exportable: true,
        label: "Position",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      id: {
        defaultValue: undefined,
        exportable: true,
        label: "Tile id"
      },
      isHidden: {
        defaultValue: false,
        exportable: true,
        label: "Hidden",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...PARTNERS,
      rowConfigs: {
        defaultValue: [getEmptyRowConfig(ROW_CONFIGS.HEADER), getEmptyRowConfig(ROW_CONFIGS.EVEN), getEmptyRowConfig(ROW_CONFIGS.ODD), getEmptyRowConfig(ROW_CONFIGS.FOOTER)],
        exportable: true,
        label: "Customization"
      },
      showInputParamsOnTile: {
        defaultValue: true,
        exportable: true,
        label: "Parameters shown on tile",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      source: {
        defaultValue: undefined,
        exportable: false,
        label: "Source tile",
        section: CONFIGURATION_SECTIONS.SOURCE.value
      },
      tileHeight: {
        defaultValue: TILE_HEIGHT.FULL,
        exportable: true,
        label: "Height",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      tileWidth: {
        defaultValue: TILE_WIDTH.FULL,
        exportable: true,
        label: "Width",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      title: {
        defaultValue: "New statistics",
        exportable: true,
        label: "Title",
        section: CONFIGURATION_SECTIONS.STYLE.value
      },
      ...TITLE_TOOLTIP_DEFAULT,
      type: {
        defaultValue: TILE_TYPES.STATS,
        exportable: true,
        label: "Type"
      }
    },
  }

  const getParsedObjectConfig = objectConfig => {
    const obj = {}
    Object.entries(objectConfig).forEach(([propertyName, propertyValue]) => {
      if(propertyValue.isSubConfiguration) {
        obj[propertyName] = getParsedObjectConfig(propertyValue.defaultValue)
      } else {
        obj[propertyName] = propertyValue.defaultValue
      }
    })
    return obj;
  }

  const EMPTY_TILE = (() => {
    const obj = {}
    Object.entries(TILE_FIELDS_CONFIG).forEach(([tileType, tileConfig]) => {
      const tileConfigParsed = getParsedObjectConfig(tileConfig)
      obj[tileType] = tileConfigParsed;
    })
    return obj;
  })()

  const EMPTY_INPUT_PARAMS = getParsedObjectConfig(INPUT_PARAMS_FIELDS_CONFIG)

  const WORKFLOW_TILES = [TILE_TYPES.AUTO_PIPELINE, TILE_TYPES.STATUS, TILE_TYPES.DIAGRAM]
  // Tile types whose domain logic is owned by ng-insight-engine Builders.
  const ENGINE_TILE_ACCESSORS = {
    [TILE_TYPES.CODE]: (builder, tileId) => builder.codeTile(tileId),
    [TILE_TYPES.IMAGE]: (builder, tileId) => builder.imageTile(tileId),
    [TILE_TYPES.PDF]: (builder, tileId) => builder.pdfTile(tileId),
    [TILE_TYPES.STATUS]: (builder, tileId) => builder.statusTile(tileId),
    [TILE_TYPES.TEXT]: (builder, tileId) => builder.textTile(tileId)
  }
  const ENGINE_TILE_ADDERS = {
    [TILE_TYPES.CODE]: (builder, options) => builder.addCodeTile(options),
    [TILE_TYPES.IMAGE]: (builder, options) => builder.addImageTile(options),
    [TILE_TYPES.PDF]: (builder, options) => builder.addPdfTile(options),
    [TILE_TYPES.STATUS]: (builder, options) => builder.addStatusTile(options),
    [TILE_TYPES.TEXT]: (builder, options) => builder.addTextTile(options)
  }
  const ENGINE_TILE_TYPES = Object.keys(ENGINE_TILE_ACCESSORS)
  const VISUALISATION_TILES = [TILE_TYPES.IMAGE, TILE_TYPES.PDF, TILE_TYPES.CODE, TILE_TYPES.TABLE, TILE_TYPES.TEXT, TILE_TYPES.CHART, TILE_TYPES.STATS, TILE_TYPES.SEARCH]

  const getTileToExport = (tile, configObj = TILE_FIELDS_CONFIG[tile.type]) => {
    const toExport = {}
    Object.entries(tile)
      .filter(([propertyName]) => configObj[propertyName]?.exportable)
      .forEach(([propertyName, propertyValue]) => {
        if(configObj[propertyName]?.isSubConfiguration) {
          toExport[propertyName] = getTileToExport(propertyValue, configObj[propertyName]?.defaultValue)
        } else {
          toExport[propertyName] = propertyValue;
        }
      })
    return toExport;
  }

  const INPUT_PARAMS_PRESET_TYPES = {
    PARAMETER_SCOPE: {
      value: "PARAMETER_SCOPE",
      label: "Scope"
    },
    DATE_RANGE: {
      value: "DATE_RANGE",
      label: "Date"
    }
  }

  const DATE_PRESET_FORMULA_COLUMN_IDS = {
    FROM: "FromValue",
    TO: "ToValue"
  }

  const INPUT_PARAMS_DATE_RANGE_TYPES = {
    TODAY: "TODAY",
    PREVIOUS_BUSINESS_DAY: "PREVIOUS_BUSINESS_DAY",
    CURRENT_MONTH: "CURRENT_MONTH",
    PREVIOUS_MONTH: "PREVIOUS_MONTH",
    CURRENT_QUARTER: "CURRENT_QUARTER",
    PREVIOUS_QUARTER: "PREVIOUS_QUARTER",
    CURRENT_TRIMESTER: "CURRENT_TRIMESTER",
    PREVIOUS_TRIMESTER: "PREVIOUS_TRIMESTER",
    CURRENT_YEAR: "CURRENT_YEAR",
    PREVIOUS_YEAR: "PREVIOUS_YEAR",
    FORMULA: "FORMULA"
  }

  const INPUT_PARAMS_DATE_RANGE_PRESETS = [
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.TODAY, label: "Today" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_BUSINESS_DAY, label: "Previous Business Day" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_MONTH, label: "Current Month" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_MONTH, label: "Previous Month" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_QUARTER, label: "Current Quarter" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_QUARTER, label: "Previous Quarter" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_TRIMESTER, label: "Current Trimester" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_TRIMESTER, label: "Previous Trimester" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.CURRENT_YEAR, label: "Current Year" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.PREVIOUS_YEAR, label: "Previous Year" },
    { value: INPUT_PARAMS_DATE_RANGE_TYPES.FORMULA, label: "Formula" }
  ]

  const INPUT_PARAMS_VALUE_TYPES = {
    TEXT: {
      value: "TEXT",
      label: "Text"
    },
    NUMBER: {
      value: "NUMBER",
      label: "Number"
    },
    DATE: {
      value: "DATE",
      label: "Date"
    },
    DROPDOWN: {
      value: "DROPDOWN",
      label: "Dropdown"
    }
  }

  const ABSOLUTE_SIZE = 5000

  const COLOR_SCHEME = {
    LIGHT: "light",
    DARK: "dark",
    AUTO: undefined
  }


  const SELF_ACTIVATION_TILES_OPTIONS = {
    FOUR: {
      label: "First four",
      value: "4"
    },
    THREE: {
      label: "First three",
      value: "3"
    },
    TWO: {
      label: "First two",
      value: "2"
    },
    ONE: {
      label: "First",
      value: "1"
    },
    NONE: {
      label: "None",
      value: "0"
    }
  }

  const INSIGHT_FILE_FIELDS = {
    DESCRIPTION: "description",
    LOGO: "logo",
    IS_SELF_ACTIVATION_REQUIRED: "is_self_activation_required",
    SELF_ACTIVATION_TILES: "self_activation_tiles",
    SHARED: "insightShared",
    PUBLIC_VIEW: "public_view",
    PUBLIC_VIEW_EXPIRATION: "public_view_expiration",
    USE_USER_TOKEN: "use_user_token",
    TAGS: "tags",
    TYPE: "insight_type",
    DRAFT: "isDraft",
    SOURCE_NAME: "Source name",
    SOURCE_GROUP: "Source group",
    SOURCE_VERSION: "Source version"
  }

  const USER_DEFINED_TIME_OPTION = {
    label: "User timezone",
    value: "USER_DEFINED"
  }

  const CODE_TYPES = {
    BASH: {
      label: "Bash",
      value: "sh"
    },
    HTML: {
      label: "HTML/FTL",
      value: ".html,.ftl"
    },
    JSON: {
      label: "JSON",
      value: "json"
    },
    JS: {
      label: "JavaScript",
      value: "js"
    },
    PYTHON: {
      label: "Python",
      value: "py"
    },
    SQL: {
      label: "SQL",
      value: "sql"
    },
    XML: {
      label: "XML",
      value: "xml"
    },
  }

  const SAVE_ACTION_FILE_AS = {
    SOURCE: {
      value: "SOURCE",
      label: "Source file"
    },
    STATIC: {
      value: "STATIC",
      label: "Static file"
    }
  }

  const REQUEST_DATA_AVAILABLE_ENVS = ["ngpcd", "prod", "stage", "stage-dw", "dev"]

  const ALL_PROVIDERS_OPTION = {
    label: "All",
    value: "*"
  }

  // Only an explicit All uses the endpoints of all environments (/envs). No provider (tiles saved before providers existed) means the local environment
  const isAllProviders = provider => provider === ALL_PROVIDERS_OPTION.value

  // Value for the Target header: undefined for All and for no provider, so the calls go to the local environment as before
  const getProviderTarget = provider => isAllProviders(provider) ? undefined : provider

  const AUTO_PIPELINE_ADDITIONAL_VALUE_TYPES = {
    INSIGHT_TILE: {
      label: "Insight Tile",
      value: "INSIGHT_TILE"
    },
    MULTI_INSIGHT_TILE: {
      label: "Multi Insight Tile",
      value: "MULTI_INSIGHT_TILE"
    },
    PDF_TILES_PICKER: {
      label: "PDF tiles picker",
      value: "PDF_TILES_PICKER"
    }
  }

  const FORWARD_CURVE_KEY = {
    SYMBOL: "Symbol",
    PRODUCT: "Product",
    ROOT: "FuturesRoot"
  }

  const CURVE_CHART_CSV_ROW_INDEXES = {
    GROUP_NAME: 0,
    FUTURES_ROOT: 1,
    PRODUCT: 2,
    SYMBOL_COLUMN: 3,
    DATE: 4
  }
  const getCurveChartCsvRowIndexes = rows => {
    const getRowIndex = ({ fallback, label }) => {
      const rowIndex = (rows || []).findIndex(row => row?.[0] === label)
      return rowIndex > -1 ? rowIndex : fallback
    }

    return {
      GROUP_NAME: getRowIndex({ fallback: CURVE_CHART_CSV_ROW_INDEXES.GROUP_NAME, label: "Group name" }),
      FUTURES_ROOT: getRowIndex({ fallback: CURVE_CHART_CSV_ROW_INDEXES.FUTURES_ROOT, label: "FuturesRoot" }),
      PRODUCT: getRowIndex({ fallback: CURVE_CHART_CSV_ROW_INDEXES.PRODUCT, label: "Product" }),
      SYMBOL_COLUMN: getRowIndex({ fallback: CURVE_CHART_CSV_ROW_INDEXES.SYMBOL_COLUMN, label: "Column" }),
      DATE: getRowIndex({ fallback: CURVE_CHART_CSV_ROW_INDEXES.DATE, label: "Dates" })
    }
  }

  const TABLE_BORDERS = {
    LEFT: "borderLeft",
    RIGHT: "borderRight",
    TOP: "borderTop",
    BOTTOM: "borderBottom"
  }

  const TABLE_BORDERS_OPTIONS = [
    { label: "Right", value: TABLE_BORDERS.RIGHT },
    { label: "Bottom", value: TABLE_BORDERS.BOTTOM },
    { label: "Left", value: TABLE_BORDERS.LEFT, tooltip: "Only for left table border.", tooltipPosition: "left" },
    { label: "Top", value: TABLE_BORDERS.TOP, tooltip: "Only for top table border.", tooltipPosition: "left" },
  ]

  const DEFAULT_CSV_HEADERS_CURVE_CHART = [{
    label: "Group",
    name: DataStorageEnums.CSV_HEADER_STATICS.GROUP_NAME,
    value: DataStorageEnums.CSV_HEADER_STATICS.GROUP_NAME,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  },{
    label: FORWARD_CURVE_KEY.ROOT,
    name: FORWARD_CURVE_KEY.ROOT,
    value: FORWARD_CURVE_KEY.ROOT,
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  },{
    label: FORWARD_CURVE_KEY.PRODUCT,
    name: FORWARD_CURVE_KEY.PRODUCT,
    value: FORWARD_CURVE_KEY.PRODUCT,
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }, {
    label: "Column",
    name: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    value: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }]


  const DEFAULT_CSV_HEADERS_CURVE_TABLE = [{
    label: "Group",
    name: DataStorageEnums.CSV_HEADER_STATICS.GROUP_NAME,
    value: DataStorageEnums.CSV_HEADER_STATICS.GROUP_NAME,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }, {
    label: FORWARD_CURVE_KEY.ROOT,
    value: FORWARD_CURVE_KEY.ROOT,
    name: FORWARD_CURVE_KEY.ROOT,
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }, {
    label: FORWARD_CURVE_KEY.PRODUCT,
    value: FORWARD_CURVE_KEY.PRODUCT,
    name: FORWARD_CURVE_KEY.PRODUCT,
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }, {
    label: "Column",
    value: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    name: DataStorageEnums.CSV_HEADER_STATICS.SYMBOL_COLUMN,
    type: DataStorageEnums.CSV_HEADER_TYPE.DEFAULT
  }, {
    name: "Description",
    label: "Description",
    value: "Description",
    type: DataStorageEnums.CSV_HEADER_TYPE.METADATA_KEY
  }]
  const IF_EMPTY_DEFAULT_CSV_CURVE_TABLE_HEADERS = [DEFAULT_CSV_HEADERS_CURVE_TABLE[1], DEFAULT_CSV_HEADERS_CURVE_TABLE[3]]

  const EMPTY_MAP_DATA_SET_SYMBOL = {
    autoGenerated: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Auto generated"
    },
    autoGenOperation: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Auto generated operation"
    },
    connectionType: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Connection type"
    },
    destGeometryName: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Destination geometry name"
    },
    destLatSource: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Destination latitude source"
    },
    destLonSource: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Destination longitude source"
    },
    fixedValue: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Selected value"
    },
    geojson: {
      defaultValue: undefined,
      exportable: true,
      label: "Geographical distribution"
    },
    geojsonType: {
      defaultValue: undefined,
      exportable: true,
      label: "Shape type"
    },
    geometryName: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Geometry name"
    },
    latSource: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Latitude source"
    },
    lonSource: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Longitude source"
    },
    on: {
      defaultValue: undefined,
      exportable: true,
      label: "Key connected to map"
    },
    originLatSource: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Origin latitude source"
    },
    originLonSource: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Origin longitude source"
    },
    originGeometryName: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Origin geometry name"
    },
    shapeSource: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Shape source"
    },
    staticRadius: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Static points size"
    },
    staticRadiusValue: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ Point radius value"
    },
    userSelectable: {
      defaultValue: undefined,
      exportable: true,
      label: "↳ User selectable"
    },
  }
  
  const EMPTY_MAP_DATA_SET = {
    chartAxis: {
      defaultValue: undefined,
      exportable: true,
      label: "Dataset axis on chart"
    },
    chartColor: {
      defaultValue: undefined,
      exportable: true,
      label: "Dataset color on chart"
    },
    chartType: {
      defaultValue: undefined,
      exportable: true,
      label: "Dataset type on chart"
    },
    colorConfig: {
      defaultValue: undefined,
      exportable: false,
      label: "Data intervals or values (max to min)"
    },
    colorConfiguration: {
      defaultValue: undefined,
      exportable: false,
      label: "Data intervals or values (max to min)"
    },
    colors: {
      defaultValue: undefined,
      exportable: false,
      label: "Colors"
    },
    column: {
      defaultValue: undefined,
      exportable: false,
      label: "Source column"
    },
    displayAsLegend: {
      defaultValue: undefined,
      exportable: true,
      label: "Display as map legend"
    },
    groupName: {
      defaultValue: undefined,
      exportable: true,
      label: "Group"
    },
    symbolList: {
      defaultValue: undefined,
      exportable: true,
      label: "Symbols"
    },
  }

  const EMPTY_MAP_CONFIG = {
    dataprep: {
      defaultValue: undefined,
      exportable: false,
      label: "Source data prep:",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    dateFormat: {
      defaultValue: undefined,
      exportable: true,
      label: "Date format",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    defaultSliderValue: {
      defaultValue: undefined,
      exportable: true,
      label: "Default date:",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    isDatePickerDropdown: {
      defaultValue: false,
      exportable: true,
      label: "Date picker",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    mapPosition: {
      defaultValue: undefined,
      exportable: true,
      label: "Default map position:",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    mapType: {
      defaultValue: undefined,
      exportable: true,
      label: "Map variant:",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    tooltip: {
      defaultValue: undefined,
      exportable: true,
      label: "Show tooltip on geometry chart",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    withMetadata: {
      defaultValue: false,
      exportable: true,
      label: "Include metadata",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
    zoomOnClick: {
      defaultValue: true,
      exportable: true,
      label: "Zoom on geometry click",
      section: CONFIGURATION_SECTIONS.BASIC_CONFIGURATION.value
    },
  }

  const EMPTY_MAP_SUMMARY = {
    autoGenerated: {
      defaultValue: undefined,
      exportable: false,
      label: "Auto generate"
    },
    autoGeneratedOperation: {
      defaultValue: undefined,
      exportable: false,
      label: "↳ Operation"
    },
    change: {
      defaultValue: undefined,
      exportable: false,
      label: "Change as"
    },
    changeDescription: {
      defaultValue: undefined,
      exportable: false,
      label: "Change description"
    },
    chartColor: {
      defaultValue: undefined,
      exportable: false,
      label: "↳ Chart color"
    },
    column: {
      defaultValue: undefined,
      exportable: false,
      label: "Source column"
    },
    groupName: {
      defaultValue: undefined,
      exportable: false,
      label: "Group"
    },
    unit: {
      defaultValue: undefined,
      exportable: false,
      label: "Data unit:"
    },
    unitOnFront: {
      defaultValue: false,
      exportable: true,
      label: "↳ Display unit:"
    },
    subTitle: {
      defaultValue: undefined,
      exportable: true,
      label: "Subtitle"
    },
    title: {
      defaultValue: undefined,
      exportable: true,
      label: "Title"
    },
    withChart: {
      defaultValue: false,
      exportable: true,
      label: "Display chart"
    },
  }

  const ALL_PARAMS = Object.fromEntries([
    ...Object.values(TILE_FIELDS_CONFIG).flatMap(tileParams => Object.entries(tileParams)),
    ...Object.entries(INPUT_PARAMS_FIELDS_CONFIG),
    ...Object.entries(CHART_AXIS_CONFIG),
    ...Object.entries(PARTNERS),
    ...Object.entries(ACTIONS),
    ...Object.entries(CHART_OUTPUT_CONFIG_DEFAULT),
    ...Object.entries(TITLE_TOOLTIP_DEFAULT),
    ...Object.entries(EMPTY_AUTO_PIPELINE_PARAM),
    ...Object.entries(EMPTY_CONDITION_FORMATTING_CURVE_ITEM),
    ...Object.entries(EMPTY_CONDITION_FORMATTING_AREA_DIFF_ITEM),
    ...Object.entries(EMPTY_HEADER_BOX),
    ...Object.entries(EMPTY_FOOTER_BUTTON),
    ...Object.entries(EMPTY_ADVANCED_SORTING_ITEM),
    ...Object.entries(EMPTY_CONDITION_FORMATTING_ITEM),
    ...Object.entries(EMPTY_REFERENCE_DATA_ITEM),
    ...Object.entries(SINGLE_ACTION),
    ...Object.entries(EMPTY_PARTNER),
    ...Object.entries(EMPTY_CHART_DATASET),
    ...Object.entries(EMPTY_INPUT_PARAM_ITEM),
    ...Object.entries(EMPTY_SYMBOL),
    ...Object.entries(EMPTY_SEASONALITY_DATA),
    ...Object.entries(EMPTY_SEARCH_FILTER),
    ...Object.entries(EMPTY_STATUS_FILTER),
    ...Object.entries(EMPTY_STATUS_PIPELINE_ITEM),
    ...Object.entries(EMPTY_COLUMN_CONFIG),
    ...Object.entries(EMPTY_TABLE_ACTION),
    ...Object.entries(EMPTY_CURVE_SET),
    ...Object.entries(EMPTY_FOOTER),
    ...Object.entries(EMPTY_MAP_DATA_SET_SYMBOL),
    ...Object.entries(EMPTY_MAP_DATA_SET),
    ...Object.entries(EMPTY_MAP_CONFIG),
    ...Object.entries(EMPTY_MAP_SUMMARY),
    ...Object.entries(EMPTY_PASSED_FILE),
    ...Object.entries(EMPTY_DIAGRAM_NODE),
    ...Object.entries(DEFAULT_FORMULA_PANEL),
    ...Object.entries(EMPTY_FORMULA_PANEL_METADATA_KEY),
    ...Object.entries(EMPTY_FUNCTIONS_FILE),
    ...Object.entries(EMPTY_DIAGRAM_CONNECTION),
    ...Object.entries(EMPTY_DIAGRAM_ATTRUBUTE),
  ])

  const COMMON_TILE_ENTRIES = []
        .concat(Object.entries(PARTNERS))
        .concat(Object.entries(ACTIONS))
        .concat(Object.entries(TITLE_TOOLTIP_DEFAULT))
        .concat(Object.entries(SINGLE_ACTION))
        .concat(Object.entries(EMPTY_PARTNER))
  const typeToConfigsResolver = type => {
    if(type === undefined || type === null) {
      return Object.entries(ALL_PARAMS)
    }

    switch(type) {
      case TILE_TYPES.TEXT:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
      case TILE_TYPES.CHART:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(CHART_AXIS_CONFIG))
          .concat(Object.entries(CHART_OUTPUT_CONFIG_DEFAULT))
          .concat(Object.entries(EMPTY_CONDITION_FORMATTING_CURVE_ITEM))
          .concat(Object.entries(EMPTY_CONDITION_FORMATTING_AREA_DIFF_ITEM))
          .concat(Object.entries(EMPTY_CONDITION_FORMATTING_ITEM))
          .concat(Object.entries(EMPTY_REFERENCE_DATA_ITEM))
          .concat(Object.entries(EMPTY_CHART_DATASET))
          .concat(Object.entries(EMPTY_SYMBOL))
          .concat(Object.entries(EMPTY_SEASONALITY_DATA))
          .concat(Object.entries(EMPTY_CURVE_SET))
      case TILE_TYPES.TABLE:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(EMPTY_CONDITION_FORMATTING_CURVE_ITEM))
          .concat(Object.entries(EMPTY_CONDITION_FORMATTING_ITEM))
          .concat(Object.entries(EMPTY_ADVANCED_SORTING_ITEM))
          .concat(Object.entries(EMPTY_SYMBOL))
          .concat(Object.entries(EMPTY_COLUMN_CONFIG))
          .concat(Object.entries(EMPTY_TABLE_ACTION))
          .concat(Object.entries(EMPTY_CURVE_SET))
          .concat(Object.entries(EMPTY_ROW_CONFIG_TABLE))
          .concat(Object.entries(EMPTY_FUNCTIONS_FILE))
          .concat(Object.entries(DEFAULT_FORMULA_PANEL))
      case TILE_TYPES.CODE:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(EMPTY_PASSED_FILE))
      case TILE_TYPES.FTL:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
      case TILE_TYPES.PDF:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
      case TILE_TYPES.IMAGE:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
      case TILE_TYPES.AUTO_PIPELINE:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(EMPTY_AUTO_PIPELINE_PARAM))
      case TILE_TYPES.STATUS:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(EMPTY_STATUS_FILTER))
          .concat(Object.entries(EMPTY_STATUS_PIPELINE_ITEM))
      case TILE_TYPES.DIAGRAM:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(EMPTY_DIAGRAM_NODE))
          .concat(Object.entries(EMPTY_DIAGRAM_CONNECTION))
          .concat(Object.entries(EMPTY_DIAGRAM_ATTRUBUTE))
      case TILE_TYPES.SEARCH:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(EMPTY_SEARCH_FILTER))
      case TILE_TYPES.STATS:
        return Object.entries(TILE_FIELDS_CONFIG[type]).concat(COMMON_TILE_ENTRIES)
          .concat(Object.entries(EMPTY_CONDITION_FORMATTING_ITEM))
          .concat(Object.entries(EMPTY_COLUMN_CONFIG))
          .concat(Object.entries(EMPTY_TABLE_ACTION))
          .concat(Object.entries(EMPTY_ROW_CONFIG_TABLE))
      case TILE_TYPES.HEADER:
        return Object.entries(EMPTY_HEADER_BOX)
          .concat(EMPTY_SYMBOL)
      case TILE_TYPES.FOOTER:
        return Object.entries(EMPTY_FOOTER)
          .concat(Object.entries(EMPTY_FOOTER_BUTTON))
      case TILE_TYPES.MAP:
        return Object.entries(EMPTY_MAP_DATA_SET_SYMBOL)
          .concat(Object.entries(EMPTY_MAP_DATA_SET))
          .concat(Object.entries(EMPTY_MAP_CONFIG))
          .concat(Object.entries(EMPTY_MAP_SUMMARY))
          .concat(Object.entries(EMPTY_SYMBOL))
      case TILE_TYPES.INPUT_PARAMS:
        return Object.entries(INPUT_PARAMS_FIELDS_CONFIG)
          .concat(Object.entries(EMPTY_INPUT_PARAM_ITEM))
          .concat(Object.entries(EMPTY_SYMBOL))
          .concat(Object.entries(EMPTY_CURVE_SET))
      default: {
        console.info("Not found type: ", type)
        return Object.entries(ALL_PARAMS);
      }
    }
  }

  const getParam = ({ configObject, paramName, type }) => {
    let param = configObject[paramName]
    if(!param) {
      if(properties.version === "LOCALHOST") {
        console.info(`Could not find ${paramName} in ${type}`)
      }
      param = ALL_PARAMS[paramName]
      if(!param) {
        if(properties.version === "LOCALHOST") {
          console.info(`Could not find ${paramName} in all params`)
        }
      }
    }
    return param;
  }

  const getParamLabel = ({ paramName, type }) => {
    const configObject = Object.fromEntries(typeToConfigsResolver(type)),
          param = getParam({ configObject, paramName, type })
    return param?.label || paramName
  }

  const getParamDefaultValue = ({ paramName, type }) => {
    const configObject = Object.fromEntries(typeToConfigsResolver(type)),
          param = getParam({ configObject, paramName, type })
    return param?.defaultValue || undefined
  }

  const getParamMapper = ({ paramName, type }) => {
    const configObject = Object.fromEntries(typeToConfigsResolver(type)),
          param = getParam({ configObject, paramName, type })
    return param?.mapper || undefined
  }

  const getSection = ({ paramName, type }) => {
    const configObject = Object.fromEntries(typeToConfigsResolver(type)),
          param = getParam({ configObject, paramName, type }),
          section = param?.section,
          sectionReadable = Object.values(CONFIGURATION_SECTIONS).find(c => c.value === section)?.label || section
    return sectionReadable || param?.label || paramName
  }

  const CUSTOM_DATE_RANGES = {
    SPECIFIC_DATE: {
      label: "Specific date",
      value: "SPECIFIC_DATE"
    },
    TODAY: {
      label: "Today",
      value: "TODAY"
    }
  }

  const ADDITIONAL_SAVE_TILE_ACTIONS = {
    ACTIVE_FORMULA: "ACTIVE_FORMULA"
  }

  const LIGHT_DRAFT_ACTION_COLOR = "#a76400"
  const DARK_DRAFT_ACTION_COLOR = "#d08b25"
  const DRAFT_PRIMARY_BUTTON_STYLE = (() => {
    let isDarkMode = false
    try {
      isDarkMode = CommonService.isDarkMode()
    } catch {
      isDarkMode = false;
    }
    return { backgroundColor: isDarkMode ? DARK_DRAFT_ACTION_COLOR : LIGHT_DRAFT_ACTION_COLOR }
  })()
  const DRAFT_SECONDARY_BUTTON_STYLE = (() => {
    let isDarkMode = false
    try {
      isDarkMode = CommonService.isDarkMode()
    } catch {
      isDarkMode = false;
    }
    return { borderColor: isDarkMode ? DARK_DRAFT_ACTION_COLOR : LIGHT_DRAFT_ACTION_COLOR }
  })()

  const DAYS_OF_WEEK = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]
  const RENDER_READINESS_PARTS = {
    CONTENT: "content",
    CHART: "chart"
  }
  const RENDER_READINESS_CONFIGS = {
    CONTENT: {
      parts: [RENDER_READINESS_PARTS.CONTENT]
    },
    CHART: {
      parts: [RENDER_READINESS_PARTS.CHART]
    }
  }

  const REQUEST_TYPES = {
    DATALAKE_FILE: "DATALAKE_FILE",
    DATASTORAGE_DATA: "DATASTORAGE_DATA",
    CURVE: "CURVE"
  }

  return {
    BASIC_CHART_TYPES,
    BASIC_INPUT_PARAM_TYPES,
    BASIC_SEARCH_TYPES,
    CHART_SOURCES,
    CHART_TYPES,
    SQL_EDITOR_STATUSES,
    TILE_TYPES,
    ACTION_TYPES,
    CONFIGS,
    EMPTY_COLOR_PALETTE,
    DEFAULT_COLOR_PALETTES,
    DEFAULT_CHART_COLOR_PALETTE,
    DEFAULT_MAP_COLOR_PALETTE,
    DEFAULT_AREA_DIFF_COLOR_SCALE,
    EMPTY_INPUT_PARAMS,
    EMPTY_TILE,
    ENGINE_TILE_ACCESSORS,
    ENGINE_TILE_ADDERS,
    ENGINE_TILE_TYPES,
    EMPTY_FOOTER,
    BUTTON_TYPES,
    EMPTY_HEADER_BOX,
    DATALAKE_FILE_HANDLER,
    STATUS_FILTER_TYPES,
    STATUS_SELECTED_BY,
    WORKFLOW_TILES,
    VISUALISATION_TILES,
    TILE_HEIGHT,
    TILE_WIDTH,
    STATUS_MAX_LIMIT,
    INPUT_PARAMS_VALUE_TYPES,
    INPUT_PARAMS_PRESET_TYPES,
    INPUT_PARAMS_DATE_RANGE_PRESETS,
    INPUT_PARAMS_DATE_RANGE_TYPES,
    DATE_PRESET_FORMULA_COLUMN_IDS,
    ABSOLUTE_SIZE,
    INPUT_PARAMS_FIXED_OPTIONS_TYPES,
    TILE_NAME_REGEXP,
    COLOR_SCHEME,
    LABEL_POSITIONS,
    SELF_ACTIVATION_TILES_OPTIONS,
    INSIGHT_FILE_FIELDS,
    INIT_CONTENT,
    INIT_FILE_INFO,
    getTileToExport,
    USER_DEFINED_TIME_OPTION,
    AUTO_PIPELINE_RESULTS_ACTION,
    AUTO_PIPELINE_RESULTS_ACTIONS,
    UNITS,
    MAP_TYPES,
    DOMAIN,
    DOMAINS,
    FORMAT_DOMAIN_OPERATIONS,
    HORIZONTAL_ALIGNS_OBJECT,
    HORIZONTAL_ALIGNS,
    CODE_TYPES,
    TABLE_CONTEXT_MENU_ACTIONS,
    TABLE_CONTEXT_MENU_ACTIONS_PLACEMENT_OPTIONS,
    TILE_SOURCE_TYPES,
    TARGET_COLUMN_OPTIONS,
    TABLE_CONTEXT_MENU_PREVIEW_FILE_TYPE,
    SAVE_ACTION_FILE_AS,
    REQUEST_DATA_AVAILABLE_ENVS,
    ALL_PROVIDERS_OPTION,
    isAllProviders,
    getProviderTarget,
    ON_TOO_SMALL_PALETTE_OPTIONS,
    AUTO_PIPELINE_ADDITIONAL_VALUE_TYPES,
    FORWARD_CURVE_KEY,
    CURVE_CHART_CSV_ROW_INDEXES,
    getCurveChartCsvRowIndexes,
    INPUT_PARAMS_FIELDS_CONFIG,
    TABLE_BORDERS_OPTIONS,
    TABLE_BORDERS,
    DEFAULT_CSV_HEADERS_CURVE_CHART,
    DEFAULT_CSV_HEADERS_CURVE_TABLE,
    FORMULA_SOURCE_CURVE_CSV_HEADER,
    FORMULA_SOURCE_TS_CSV_HEADER,
    isFormulaSourceInputParamsConfig,
    isFormulaSourceTable,
    IF_EMPTY_DEFAULT_CSV_CURVE_TABLE_HEADERS,
    CODE_TILE_SAVE_FILE_TYPES,
    ACTIVE_PANEL,
    TILE_FIELDS_CONFIG,
    CHART_AXIS_CONFIG,
    PARTNERS,
    ACTIONS,
    CHART_OUTPUT_CONFIG_DEFAULT,
    TITLE_TOOLTIP_DEFAULT,
    EMPTY_AUTO_PIPELINE_PARAM,
    EMPTY_CONDITION_FORMATTING_CURVE_ITEM,
    EMPTY_CONDITION_FORMATTING_AREA_DIFF_ITEM,
    EMPTY_FOOTER_BUTTON,
    EMPTY_ADVANCED_SORTING_ITEM,
    EMPTY_CONDITION_FORMATTING_ITEM,
    SINGLE_ACTION,
    EMPTY_PARTNER,
    EMPTY_CHART_DATASET,
    EMPTY_INPUT_PARAM_ITEM,
    EMPTY_SYMBOL,
    EMPTY_SEASONALITY_DATA,
    EMPTY_SEARCH_FILTER,
    EMPTY_STATUS_FILTER,
    EMPTY_STATUS_PIPELINE_ITEM,
    EMPTY_COLUMN_CONFIG,
    EMPTY_TABLE_ACTION,
    EMPTY_CURVE_SET,
    CONFIGURATION_SECTIONS,
    EMPTY_MAP_DATA_SET_SYMBOL,
    EMPTY_MAP_DATA_SET,
    EMPTY_MAP_CONFIG,
    EMPTY_MAP_SUMMARY,
    EMPTY_PASSED_FILE,
    EMPTY_DIAGRAM_NODE,
    EMPTY_FORMULA_PANEL_METADATA_KEY,
    DEFAULT_FORMULA_PANEL,
    FORMULA_CONSTANTS_SOURCES,
    FORMULA_PANEL_DEFAULT_VALUE,
    FORMULA_PANEL_METADATA_VALUE_TYPE,
    FORMULA_PANEL_METADATA_VALUE_TYPES,
    FORMULA_PANEL_USE_FORMULA_NAME_OPTION,
    METADATA_STANDARD_GROUP,
    NGPCD,
    EMPTY_DIAGRAM_CONNECTION,
    EMPTY_DIAGRAM_ATTRUBUTE,
    getParam,
    getParamLabel,
    getParamDefaultValue,
    getParamMapper,
    getSection,
    CUSTOM_DATE_RANGES,
    ADDITIONAL_SAVE_TILE_ACTIONS,
    DRAFT_PRIMARY_BUTTON_STYLE,
    DRAFT_SECONDARY_BUTTON_STYLE,
    SEASONALITY_TYPE,
    SEASONALITY_PICKERS_DATA_LABEL_TYPES,
    DATA_POINT_LABEL_VALUE_TYPES,
    SEASONALITY_PICKERS_CONTRACT_RESOLVER_TYPES,
    CONTRACT_RESOLVER_CONFIG_FIELD_NAMES,
    DAYS_ZOOM_PARAM,
    DAYS_ZOOM_MIN,
    DAYS_ZOOM_MAX,
    EXPIRY_DATE_FORMAT,
    EXPIRY_DATE_PARAM,
    EXPIRY_DATE_REFERENCE_LINE_KEY,
    SPREAD_BAND_KEY,
    SPREAD_LOWER_BAND_KEY,
    SPREAD_MEDIAN_KEY,
    SPREAD_UPPER_BAND_KEY,
    APPLY_TO_RADIO,
    CHART_INTERPOLATIONS,
    AXIS_TYPES,
    LINE_STYLES,
    CHART_LAYOUTS,
    CHART_LAYOUT,
    LABEL_BASED_ON,
    SEASON_MONTHS_OPTIONS,
    SEASON_MONTH,
    NORMALIZATION_METHODS,
    DIAGRAM_NODES,
    AVAILABLE_ACTIONS,
    CONNECTION_TYPES,
    MAP_POSITIONS,
    COLUMN_TYPES,
    COLUMN_TYPES_ARRAY,
    READ_ONLY_COLUMN_TYPES_ARRAY,
    ROUNDING_POLICIES,
    ROUNDING_POLICY,
    ROW_CONFIGS,
    AXIS_POSITION,
    getAxisSelectionValue,
    getSeasonalityYearOptions,
    getSeasonalityYearValue,
    REFERENCE_BAND_KEY_PREFIX,
    REFERENCE_BAND_LABELS,
    REFERENCE_BAND_DEFAULTS,
    REFERENCE_DATA_TYPES,
    REFERENCE_LINE_AXES,
    REFERENCE_LINE_KEY_PREFIX,
    REFERENCE_X_AXIS_KINDS,
    REFERENCE_X_VALUES,
    REFERENCE_Y_VALUE_TYPES,
    SPREAD_BAND_RANGE,
    SPREAD_BAND_USER_SELECTION,
    BAND_BASED_ON,
    APPLY_TO,
    FORMULA_RECALCULATE_PERIODS,
    FORMULA_RECALCULATE_MODES,
    DAYS_OF_WEEK,
    RENDER_READINESS_PARTS,
    RENDER_READINESS_CONFIGS,
    REQUEST_TYPES
  }
})()
